import React, { useState, useRef, useEffect, useMemo, memo, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Image,
  ImageBackground,
  Platform,
  Dimensions,
  Pressable,
  Modal,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { SafeAreaView } from "react-native-safe-area-context";
import { useQuery, usePaginatedQuery, useMutation, useConvex } from "convex/react";
import { api } from "../convex/_generated/api";
import { useAuthData } from "../hooks/useAuthData";
import { usePrivy } from "@privy-io/react-auth";
import { useNavigation } from "../utils/NavigationContext";
import MapComponent from "../components/MapScreen/MapComponent.web";
import {
  BottomSheetContent,
  GooglePlacesSearchResults,
  AddRestaurantModal,
  HousePickButton,
  RecapButton,
  MapHeavenHillsButton,
  PalomaPaloozaSheetHeader,
  PassesExplorePill,
  PassesAvailabilityBadge,
  PassCard,
  PassClaimStatusWrapper,
  PassUseModal,
  GiftPassWorkflow,
} from "../components";
import { fontFamily } from "../constants/fonts";
import { isCurrentlyHappyHour } from "../utils/timeUtils";
import { useSpeedRailzMapPromoUiEnabled } from "../utils/speedRailzMapUi";
import { useHeavenHillsMapPromoUiEnabled } from "../utils/heavenHillsMapUi";
import { useDisgoPassesEnabled } from "../utils/disgoPassesFlag";
import { Alert, Linking } from "react-native";
import { useGooglePlacesSearch } from "../hooks/useGooglePlacesSearch";
import { useGooglePlaceDisplay } from "../hooks/useGooglePlaceDisplay";
import { calculateDistance, formatDistance } from "../utils/googlePlaces";
import { combineGooglePrimarySearchResults } from "../utils/combineGooglePrimarySearchResults";
import {
  CHECKIN_DISTANCE_METERS,
  completeRemoteRecapCheckIn,
} from "../utils/remoteRecapCheckIn";
import { useHousePickDistanceUnit } from "../hooks/useHousePickDistanceUnit";
import { getCurrentPosition } from "../utils/geolocation";
import { handleDisgoPassesButtonPress } from "../utils/disgoPassesButtonAction";
import { filterUnusedPasses, useUnusedPasses, getPassVenueNamesLine } from "../utils/passFiltering";
import BeRealCheckIn from "../src/components/beRealCheckIn";
import DiscoSuccessModal from "../src/components/DiscoSuccessModal";
import { openDirections } from "../utils/mapNavigation";
import { omitDeprecatedRestaurantPlaceFields } from "../utils/omitDeprecatedRestaurantPlaceFields";
import { colors } from "../constants/theme";
import { PALOMA_NAVY, PALOMA_ACCENT, PALOMA_ROW_BG } from "../constants/palomaPaloozaBrand";
import {
  SPEEDRAILZ_SHEET_TITLE_LINE1,
  SPEEDRAILZ_SHEET_TITLE_LINE2,
} from "../constants/speedRailzPromo";
import FilterBottomSheetContent from "../components/FilterBottomSheetContent/FilterBottomSheetContent";
import RecapMomentScreen from "./RecapMomentScreen";
import RecapRateMomentScreen from "./RecapRateMomentScreen";
import CheckInScreen from "./CheckIn/CheckInScreen";

const PP_LOGO = require("../assets/paloma-palooza-logo.png");

// Hook for responsive design
const useResponsive = () => {
  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  const isMobile = dimensions.width < 768;

  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });

    return () => subscription?.remove();
  }, []);

  return { isMobile, width: dimensions.width };
};

/** Copy for the “get the app” sheet (web modal + profile tab card). */
const DOWNLOAD_APP_FEATURES = [
  { icon: "camera-outline", text: "Check in, add photos, and finish your recaps" },
  { icon: "people-outline", text: "See friends on the map and what they rated" },
  { icon: "bookmark-outline", text: "Save spots to private lists and keep them across sessions" },
  { icon: "wine-outline", text: "Happy hours, passes, and daily specials" },
];

/** Degrees north — map center offset when focusing a venue (same as native MapScreen). */
const MAP_FOCUS_LATITUDE_OFFSET = 0.004;

// Loading Skeleton Component
const RestaurantSkeleton = memo(() => {
  return (
    <View style={styles.skeletonContainer}>
      <View style={styles.skeletonImage} />
      <View style={styles.skeletonContent}>
        <View style={styles.skeletonLine1} />
        <View style={styles.skeletonLine2} />
        <View style={styles.skeletonLine3} />
      </View>
    </View>
  );
});

RestaurantSkeleton.displayName = "RestaurantSkeleton";

// Memoized Restaurant Item Component matching native design
const RestaurantItem = memo(
  ({
    item,
    onPress,
    currentLocation,
    friendsAverageRatings,
    userCheckinHistory,
    dailySpecials,
    hasGiftablePassesForRestaurant,
    getFriendsRatingBadge,
    formatDistance,
    isSpeedRailzContext = false,
    isHeavenHillsContext = false,
    leaderboardRank = null,
    leaderboardPoints = null,
  }) => {
    const today = new Date().toLocaleString("en-US", { weekday: "long" });
    const todaysSpecial = dailySpecials?.find(
      (special) => special.restaurantId === item._id && special.dayOfWeek === today
    );
    const isHappyHour = todaysSpecial ? isCurrentlyHappyHour(todaysSpecial.whenHH) : false;
    const hasCheckedIn = userCheckinHistory?.includes(item._id) || false;
    const hasGiftable = hasGiftablePassesForRestaurant(item._id);
    const distanceKm = currentLocation
      ? calculateDistance(
        currentLocation.latitude,
        currentLocation.longitude,
        item.latitude,
        item.longitude
      )
      : null;
    const distanceText = distanceKm !== null ? formatDistance(distanceKm) : null;
    const rating = friendsAverageRatings[item._id];
    const ratingBadge = rating?.averageRating ? getFriendsRatingBadge(rating.averageRating) : null;

    const { name: googleName, photoUrl: googlePhotoUrl } = useGooglePlaceDisplay(item.googlePlaceId);
    const displayName = googleName || item.name;

    // Check if thumbnail/imgUrl is a storage ID and convert if needed (DB fallback only)
    const imageSource = item.thumbnail || item.imgUrl;
    const isFullUrl = imageSource && (imageSource.startsWith("http://") || imageSource.startsWith("https://"));
    const isStorageId =
      imageSource &&
      !isFullUrl &&
      (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(imageSource) ||
        /^[a-z0-9]{20,}$/i.test(imageSource));

    const imageUrl = useQuery(
      isStorageId && imageSource ? api.images.getStorageUrl : "skip",
      isStorageId && imageSource ? { storageId: imageSource } : "skip"
    );

    const finalImageUri = isStorageId ? imageUrl || null : imageSource;
    const listImageUri = googlePhotoUrl || (item.isPartner ? finalImageUri : null);
    const [imageError, setImageError] = useState(false);

    if (leaderboardRank !== null) {
      // Leaderboard view: Only show rank, name, and points
      return (
        <TouchableOpacity
          onPress={() => onPress(item)}
          style={[
            styles.restaurantItem,
            isSpeedRailzContext && item.isSpeedRailzPartner && styles.restaurantItemSpeedRailz,
            isHeavenHillsContext && item.isHeavenHills && styles.restaurantItemHeavenHills,
          ]}
        >
          <View style={styles.restaurantInfo}>
            <View style={styles.restaurantNameRow}>
              <Text style={styles.restaurantName}>#{leaderboardRank}</Text>
              <Text style={styles.restaurantName}>{displayName}</Text>
            </View>
            {leaderboardPoints !== null && (
              <Text style={styles.restaurantMeta}>{leaderboardPoints} points</Text>
            )}
          </View>
        </TouchableOpacity>
      );
    }

    // Regular view: Show all info
    return (
      <TouchableOpacity
        onPress={() => onPress(item)}
        style={[
          styles.restaurantItem,
          isSpeedRailzContext && item.isSpeedRailzPartner && styles.restaurantItemSpeedRailz,
          isHeavenHillsContext && item.isHeavenHills && styles.restaurantItemHeavenHills,
        ]}
      >
        <View style={styles.restaurantImageContainer}>
          {listImageUri && !imageError ? (
            <Image
              source={{ uri: listImageUri }}
              style={styles.restaurantImage}
              defaultSource={require("../assets/icon.png")}
              onError={() => setImageError(true)}
            />
          ) : (
            <ImageBackground
              source={require("../assets/nonDisgoPartnerMembershipCard.jpg")}
              style={styles.restaurantImage}
              imageStyle={styles.restaurantImageStyle}
            />
          )}
          {/* Gift icon overlay */}
          {hasGiftable && (
            <View style={styles.giftIconOverlay}>
              <Ionicons name="gift" size={12} color="white" />
            </View>
          )}
          {item.isPartner && (
            <View style={styles.partnerBadgeOverlay} accessibilityLabel="Disgo Partner">
              <Text style={styles.partnerBadgeText}>D</Text>
            </View>
          )}
        </View>
        <View style={styles.restaurantInfo}>
          <View style={styles.restaurantHeader}>
            <View style={styles.restaurantNameRow}>
              <Text style={styles.restaurantName}>{displayName}</Text>
            </View>
            {currentLocation && distanceText && (
              <Text
                style={[
                  styles.restaurantDistance,
                  isSpeedRailzContext && item.isSpeedRailzPartner && styles.restaurantDistanceSpeedRailz,
                  isHeavenHillsContext && item.isHeavenHills && styles.restaurantDistanceHeavenHills,
                ]}
              >
                {distanceText}
              </Text>
            )}
          </View>
          <Text style={styles.restaurantMeta}>
            {item.cuisine}
            {item.price ? ` • ${item.price}` : ""}
            {ratingBadge
              ? ` • ${ratingBadge.emoji} ${ratingBadge.label} (${rating.averageRating.toFixed(1)})`
              : " • No friends rated"}
          </Text>
          {(hasCheckedIn || todaysSpecial) && (
            <View style={styles.happyHourContainer}>
              {hasCheckedIn && (
                <View style={styles.visitedPill} accessibilityLabel="You have visited this place" accessibilityRole="text">
                  <Text style={styles.visitedPillText}>Visited</Text>
                </View>
              )}
              {todaysSpecial && (
                <>
                  {isHappyHour && (
                    <View style={styles.happyHourBadge}>
                      <Text style={styles.happyHourBadgeText}>HH Now</Text>
                    </View>
                  )}
                  {todaysSpecial.whenHH && todaysSpecial.whenHH.toLowerCase() !== "n/a" && (
                    <View style={styles.happyHourDescription}>
                      <Text style={styles.happyHourDescriptionText}>{todaysSpecial.whenHH}</Text>
                    </View>
                  )}
                  {todaysSpecial.description && (
                    <View style={styles.happyHourDescription}>
                      <Text style={styles.happyHourDescriptionText}>
                        {todaysSpecial.description.length > 35
                          ? `${todaysSpecial.description.substring(0, 35)}...`
                          : todaysSpecial.description}
                      </Text>
                    </View>
                  )}
                </>
              )}
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.item._id === nextProps.item._id &&
      prevProps.item.googlePlaceId === nextProps.item.googlePlaceId &&
      prevProps.item.name === nextProps.item.name &&
      prevProps.item.thumbnail === nextProps.item.thumbnail &&
      prevProps.item.imgUrl === nextProps.item.imgUrl &&
      prevProps.isSpeedRailzContext === nextProps.isSpeedRailzContext &&
      prevProps.isHeavenHillsContext === nextProps.isHeavenHillsContext &&
      prevProps.leaderboardRank === nextProps.leaderboardRank &&
      prevProps.formatDistance === nextProps.formatDistance
    );
  }
);

RestaurantItem.displayName = "RestaurantItem";

function MapScreen({ navigation = null }) {
  const convex = useConvex();
  const isSpeedRailzMapPromoUiEnabled = useSpeedRailzMapPromoUiEnabled();
  const isHeavenHillsMapPromoUiEnabled = useHeavenHillsMapPromoUiEnabled();
  const showDisgoPasses = useDisgoPassesEnabled();
  const { authData, deleteAuthData } = useAuthData();
  const { logout, authenticated, ready, login } = usePrivy();
  const { isMobile } = useResponsive();
  // Use navigation context for shared navigation state
  const {
    activeTab,
    setActiveTab,
    showDownloadAppModal,
    setShowDownloadAppModal,
    showProfileModal,
    setShowProfileModal,
    showWelcomeModal,
    setShowWelcomeModal,
    showRestaurantList,
    setShowRestaurantList,
  } = useNavigation();
  const { unit: distanceDisplayUnit } = useHousePickDistanceUnit();
  const formatDistanceDisplay = useCallback(
    (d) => formatDistance(d, distanceDisplayUnit),
    [distanceDisplayUnit]
  );
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showRecapModal, setShowRecapModal] = useState(false);
  const [recapRateMomentId, setRecapRateMomentId] = useState(null);
  const [recapEditMomentId, setRecapEditMomentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedDay, setSelectedDay] = useState("All");
  const [friendRatingFilter, setFriendRatingFilter] = useState("all");
  const friendRatingFilterOptions = useMemo(
    () => [
      { value: "all", label: "All" },
      { value: "friendsRated", label: "Friends Rated" },
    ],
    []
  );
  const [showOnlySpeedRailzOnMap, setShowOnlySpeedRailzOnMap] = useState(false);
  const [showOnlyHeavenHillsOnMap, setShowOnlyHeavenHillsOnMap] = useState(false);
  const [showDisgoRestaurants, setShowDisgoRestaurants] = useState(false);
  const [showNonDisgoRestaurants, setShowNonDisgoRestaurants] = useState(false);
  const [showFriendsRatedRestaurants, setShowFriendsRatedRestaurants] = useState(false);
  const [showMyCheckedInRestaurants, setShowMyCheckedInRestaurants] = useState(false);
  const days = ["All", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [isSpeedRailzPressed, setIsSpeedRailzPressed] = useState(false);
  const [showSpeedRailzSheet, setShowSpeedRailzSheet] = useState(false);
  const [showHeavenHillsSheet, setShowHeavenHillsSheet] = useState(false);
  const [listSheetHeight, setListSheetHeight] = useState(50); // Percentage of screen height
  const [isListSheetExpanded, setIsListSheetExpanded] = useState(false);
  /** Mobile: Disgo Passes in a separate bottom sheet from the restaurant list */
  const [showPassesMobileSheet, setShowPassesMobileSheet] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [sideSheetActiveTab, setSideSheetActiveTab] = useState("main"); // Desktop side sheet tab id
  const [showDesktopSideSheet, setShowDesktopSideSheet] = useState(true); // Control side sheet visibility on desktop
  /** Last non-restaurant side sheet tab — never "restaurant", so Back always has a real pane to show */
  const [previousTab, setPreviousTab] = useState("main");
  const [profileActiveTab, setProfileActiveTab] = useState("moments"); // For profile tabs
  useEffect(() => {
    if (!showDisgoPasses && profileActiveTab === "memberships") {
      setProfileActiveTab("moments");
    }
  }, [showDisgoPasses, profileActiveTab]);

  useEffect(() => {
    if (!showDisgoPasses) {
      setShowPassesMobileSheet(false);
      setSideSheetActiveTab((t) => (t === "passes" ? "main" : t));
    }
  }, [showDisgoPasses]);
  useEffect(() => {
    if (!isSpeedRailzMapPromoUiEnabled) {
      setShowSpeedRailzSheet(false);
      setShowOnlySpeedRailzOnMap(false);
      if (sideSheetActiveTab === "speedrailz") {
        setSideSheetActiveTab("main");
      }
    }
  }, [isSpeedRailzMapPromoUiEnabled, sideSheetActiveTab]);

  useEffect(() => {
    if (!isHeavenHillsMapPromoUiEnabled) {
      setShowHeavenHillsSheet(false);
      setShowOnlyHeavenHillsOnMap(false);
      if (sideSheetActiveTab === "heavenhills") {
        setSideSheetActiveTab("main");
      }
    }
  }, [isHeavenHillsMapPromoUiEnabled, sideSheetActiveTab]);

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsPanel, setSettingsPanel] = useState(null); // null = menu, 'account' | 'notifications'
  const [accountSubPanel, setAccountSubPanel] = useState(null); // null = account menu, 'details' | 'logout' | 'delete'
  const [userInputChanges, setUserInputChanges] = useState({});
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showGooglePlacesResults, setShowGooglePlacesResults] = useState(false);
  const [showAddRestaurantModal, setShowAddRestaurantModal] = useState(false);
  const [isLoadingRestaurant, setIsLoadingRestaurant] = useState(false);
  const [pendingRestaurantId, setPendingRestaurantId] = useState(null);
  const [savedGooglePlaces, setSavedGooglePlaces] = useState(new Set());
  const [restaurantForRecap, setRestaurantForRecap] = useState(null);
  const [showBeRealCheckIn, setShowBeRealCheckIn] = useState(false);
  const [restaurantForBeRealCheckIn, setRestaurantForBeRealCheckIn] = useState(null);
  /** Snapshot of coords validated at check-in tap; used for server check-in when share completes */
  const [beRealCheckInLocationSnapshot, setBeRealCheckInLocationSnapshot] = useState(null);
  const [checkInRestaurantId, setCheckInRestaurantId] = useState(null);
  const [showDiscoModal, setShowDiscoModal] = useState(false);
  const [showUsePassModal, setShowUsePassModal] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [selectedPassForGift, setSelectedPassForGift] = useState(null);
  const [selectedRestaurantForGift, setSelectedRestaurantForGift] = useState(null);
  const [isGiftPassWorkflowVisible, setIsGiftPassWorkflowVisible] = useState(false);
  const [showTooFarModal, setShowTooFarModal] = useState(false);
  const [tooFarRestaurant, setTooFarRestaurant] = useState(null);
  const [showAlreadyCheckedInModal, setShowAlreadyCheckedInModal] = useState(false);
  const [alreadyCheckedInRestaurant, setAlreadyCheckedInRestaurant] = useState(null);
  const isLoggingOutRef = useRef(false);

  const [mapBounds, setMapBounds] = useState({
    northEast: {
      lat: 30.2672 + 0.1,
      lng: -97.7431 + 0.1,
    },
    southWest: {
      lat: 30.2672 - 0.1,
      lng: -97.7431 - 0.1,
    },
  });
  const [currentLocation, setCurrentLocation] = useState(null);

  // Show welcome modal when page loads if not authenticated (but not after logout)
  useEffect(() => {
    if (ready && !authenticated && !isLoggingOutRef.current) {
      setShowWelcomeModal(true);
    } else if (authenticated) {
      setShowWelcomeModal(false);
      isLoggingOutRef.current = false; // Reset the flag when user becomes authenticated
    }
  }, [ready, authenticated]);

  // Check if user has already granted location permission on page load
  useEffect(() => {
    if (navigator.geolocation && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted' && !currentLocation) {
          // User has already granted permission, get location automatically
          getCurrentPosition(
            (position) => {
              const location = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              };
              console.log("Auto-fetched location on page load:", location);
              setCurrentLocation(location);
              setInitialRegion({
                ...location,
                latitudeDelta: 0.02,
                longitudeDelta: 0.02,
              });
              // Center map on user location
              if (mapRef.current) {
                mapRef.current.panTo({
                  lat: location.latitude - 0.004,
                  lng: location.longitude,
                });
                mapRef.current.setZoom(15);
              }
            },
            (error) => {
              // Silently fail - permission might have been revoked
              console.log("Auto-location fetch failed:", error);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 60000 // Accept cached location up to 1 minute old
            }
          );
        }
      }).catch((error) => {
        // Permissions API not supported, skip auto-fetch
        console.log("Permissions API not supported:", error);
      });
    }
  }, [currentLocation]);
  const [initialRegion, setInitialRegion] = useState({
    latitude: 30.2672,
    longitude: -97.7431,
    latitudeDelta: 0.02,
    longitudeDelta: 0.02,
  });
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  // Get user document
  const userDoc = useQuery(
    authData?.privyDid ? api.users.getUserFromPrivyDid : "skip",
    authData?.privyDid ? { privyDid: authData.privyDid } : "skip"
  );
  const recapMoments = useQuery(
    api.moments.getMomentsForRecap,
    authData?.privyDid ? { privyDid: authData.privyDid } : "skip"
  );

  // Get user's check-in history (raw query for "my check-ins" filter loading semantics)
  const userCheckinHistoryQuery = useQuery(
    authData?.privyDid ? api.users.getUserCheckinHistory : "skip",
    authData?.privyDid ? { privyDid: authData.privyDid } : "skip"
  );
  const userCheckinHistory = Array.isArray(userCheckinHistoryQuery)
    ? userCheckinHistoryQuery
    : [];

  // Get user memberships
  const userMemberships = useQuery(
    authData?.privyDid ? api.memberships.getUserMemberships : "skip",
    authData?.privyDid ? { privyDid: authData.privyDid } : "skip"
  );

  // Get profile picture URL
  const profilePictureUrl = useQuery(
    api.images.getStorageUrl,
    userDoc?.pfp ? { storageId: userDoc.pfp } : "skip"
  );

  // Get user statistics
  const userStats = useQuery(
    api.users.getUserStats,
    authData?.privyDid ? { privyDid: authData.privyDid } : "skip"
  );

  // Create membership mutation
  const createMembership = useMutation(api.memberships.createMembership);
  const checkInWithLocation = useMutation(api.checkInWithLocation.checkInWithLocation);
  const createMoment = useMutation(api.moments.createMoment);
  const claimPass = useMutation(api.passActions.claimPass);
  const cantGo = useMutation(api.passActions.cantGo);

  // Profile editing mutations
  const updateUser = useMutation(api.users.updateUser);
  const generateUploadUrl = useMutation(api.images.generateUploadUrl);
  const saveStorageId = useMutation(api.images.saveStorageId);
  const deleteUserMutation = useMutation(api.users.deleteUser);

  const handleRecapPress = useCallback(() => {
    if (!authData?.privyDid) {
      Alert.alert("Sign in", "Sign in to view and edit your recaps.");
      return;
    }
    setRecapEditMomentId(null);
    setRecapRateMomentId(null);
    setShowRecapModal(true);
  }, [authData?.privyDid]);

  /** Desktop sidebar: recap lives in the sheet instead of a full-screen modal. */
  const handleDesktopRecapTabPress = useCallback(() => {
    if (!authData?.privyDid) {
      Alert.alert("Sign in", "Sign in to view and edit your recaps.");
      return;
    }
    setShowDesktopSideSheet(true);
    setRecapEditMomentId(null);
    setRecapRateMomentId(null);
    setSideSheetActiveTab("recap");
    setShowRecapModal(false);
  }, [authData?.privyDid]);

  const handleDesktopRecapListBack = useCallback(() => {
    setRecapEditMomentId(null);
    setRecapRateMomentId(null);
    setSideSheetActiveTab("main");
  }, []);

  // Initialize user input changes when user data loads
  useEffect(() => {
    if (userDoc) {
      setUserInputChanges({
        name: userDoc.name || "",
        username: userDoc.username || "",
        email: userDoc.email || "",
        phoneNumber: userDoc.phoneNumber || "",
      });
    }
  }, [userDoc]);

  // Check username availability
  const isUsernameAvailable = useQuery(
    api.users.isUsernameAvailable,
    userInputChanges.username && userInputChanges.username.length >= 3
      ? { username: userInputChanges.username.toLowerCase() }
      : "skip"
  );

  // Handle image picker for web
  const handleImagePicker = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          setImage(base64String);
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!authData?.privyDid) {
      Alert.alert("Error", "User not authenticated");
      return;
    }

    setIsSaving(true);
    try {
      // Upload image if selected
      if (image) {
        const base64Response = await fetch(`data:image/jpeg;base64,${image}`);
        const blob = await base64Response.blob();

        const postUrl = await generateUploadUrl({
          privyDid: authData.privyDid,
        });

        const fetch_result = await fetch(postUrl, {
          method: "POST",
          headers: { "Content-Type": "image/jpeg" },
          body: blob,
        });

        if (!fetch_result.ok) {
          throw new Error("Failed to upload image");
        }

        const { storageId } = await fetch_result.json();
        if (storageId === undefined) {
          throw new Error("Failed to get storage ID");
        }

        await saveStorageId({
          privyDid: authData.privyDid,
          storageId: storageId,
        });

        setImage(null);
        setImagePreview(null);
      }

      // Update user data
      await updateUser({
        privyDid: authData.privyDid,
        name: userInputChanges.name,
        username: userInputChanges.username.toLowerCase(),
      });

      Alert.alert(
        "Success",
        "Your profile has been updated successfully!",
        [{ text: "OK", onPress: () => setShowSettingsModal(false) }]
      );
    } catch (error) {
      console.error("Error updating profile:", error);
      Alert.alert("Error", "Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const performLogout = async () => {
    try {
      setShowProfileModal(false);
      setShowSettingsModal(false);
      setAccountSubPanel(null);
      setSettingsPanel(null);
      isLoggingOutRef.current = true;
      await deleteAuthData();
      await logout();
    } catch (error) {
      console.error("Error during logout: ", error);
      Alert.alert("Error", "Failed to log out. Please try again.");
      isLoggingOutRef.current = false;
    }
  };

  // Handle logout (Alert.alert callbacks are unreliable on react-native-web)
  const handleLogout = () => {
    if (Platform.OS === "web") {
      if (typeof window !== "undefined" && window.confirm("Are you sure you want to log out?")) {
        void performLogout();
      }
      return;
    }
    Alert.alert("Log out", "Are you sure you want to log out?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Log out",
        style: "destructive",
        onPress: () => void performLogout(),
      },
    ]);
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    Alert.alert(
      "Delete Account",
      "Are you sure you want to delete your account? This action cannot be undone. We will delete all your data and you will be logged out.",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "Delete",
          onPress: async () => {
            try {
              // First delete the user data
              await deleteUserMutation({ privyDid: authData?.privyDid });
              // Then delete local auth data
              await deleteAuthData();
              // Finally logout of Privy
              await logout();
              // The app will automatically redirect to login screen
            } catch (error) {
              console.error("Error during delete account: ", error);
              Alert.alert(
                "Error",
                "Failed to delete account. Please try again.",
                [{ text: "OK" }]
              );
            }
          },
          style: "destructive",
        },
      ]
    );
  };

  // Google Places search hook
  const {
    searchResults: googlePlacesResults,
    isSearching: isGooglePlacesSearching,
    searchError: googlePlacesError,
    search: searchGooglePlaces,
    clearSearch: clearGooglePlacesSearch,
    hasGooglePlacesResults
  } = useGooglePlacesSearch();

  // Google-primary search: enrich Google hits by place id + discover via daily_specials text
  const googlePlaceIdsForEnrichment = useMemo(
    () =>
      (googlePlacesResults || [])
        .map((place) => place.id)
        .filter((id) => typeof id === "string" && id.length > 0),
    [googlePlacesResults]
  );

  const disgoRestaurantsByPlaceId = useQuery(
    api.getRestaurants.getRestaurantsByGooglePlaceIds,
    googlePlaceIdsForEnrichment.length > 0
      ? { placeIds: googlePlaceIdsForEnrichment }
      : "skip"
  );

  const specialsRestaurants = useQuery(
    api.getRestaurants.searchRestaurantsBySpecialsText,
    debouncedSearchQuery && debouncedSearchQuery.trim().length >= 2
      ? { searchText: debouncedSearchQuery, limit: 10 }
      : "skip"
  );

  // Query to get saved Google Place IDs
  const savedGooglePlaceIds = useQuery(api.getGooglePlaces.getSavedGooglePlaceIds);

  // Update saved places when query data changes
  useEffect(() => {
    if (savedGooglePlaceIds) {
      setSavedGooglePlaces(new Set(savedGooglePlaceIds));
    }
  }, [savedGooglePlaceIds]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const combinedSearchResults = useMemo(
    () =>
      combineGooglePrimarySearchResults({
        googlePlacesResults: googlePlacesResults || [],
        disgoRestaurantsByPlaceId: disgoRestaurantsByPlaceId || [],
        specialsRestaurants: specialsRestaurants || [],
        currentLocation,
        calculateDistance,
      }),
    [
      googlePlacesResults,
      disgoRestaurantsByPlaceId,
      specialsRestaurants,
      currentLocation,
    ]
  );

  // Use paginated query for restaurants with bounds and search (for map display)
  const restaurantsResult = usePaginatedQuery(
    api.getRestaurants.getRestaurants,
    mapBounds ? {
      searchText: debouncedSearchQuery.trim() || undefined,
      bounds: mapBounds,
      paginationOpts: { numItems: 100 },
    } : "skip",
    { initialNumItems: 100 }
  );

  // Separate query for all restaurants without search filtering (for daily specials and passes)
  const allRestaurantsResult = usePaginatedQuery(
    api.getRestaurants.getRestaurants,
    mapBounds ? {
      bounds: mapBounds,
      paginationOpts: { numItems: 100 },
    } : "skip",
    { initialNumItems: 100 }
  );

  // Extract restaurants from paginated result
  const restaurants = restaurantsResult?.results || [];

  // Extract all restaurants (unfiltered) for daily specials and passes
  const allRestaurants = allRestaurantsResult?.results || [];
  const restaurantIds = useMemo(() => restaurants.map((r) => r._id).filter(Boolean), [restaurants]);
  const allRestaurantIds = useMemo(() => allRestaurants.map((r) => r._id).filter(Boolean), [allRestaurants]);

  // Query for daily specials (restaurant explore list is always specials browse)
  const dailySpecials = useQuery(
    selectedDay !== "All"
      ? api.getSpecialsByDay.getSpecialsByDay
      : api.getSpecialsByRestaurants.getSpecialsForRestaurants,
    selectedDay !== "All"
      ? { day: selectedDay }
      : allRestaurantIds && allRestaurantIds.length > 0
        ? { restaurantIds: allRestaurantIds }
        : "skip"
  ) || [];

  // Query for friends average ratings — prefer full explore list ids when loaded
  const restaurantIdsForRatings =
    allRestaurantIds && allRestaurantIds.length > 0
      ? allRestaurantIds
      : restaurantIds && restaurantIds.length > 0
        ? restaurantIds
        : null;

  const friendsAverageRatings = useQuery(
    userDoc?._id && restaurantIdsForRatings && restaurantIdsForRatings.length > 0
      ? api.friends.getFriendsAverageRatingsForRestaurants
      : "skip",
    userDoc?._id && restaurantIdsForRatings && restaurantIdsForRatings.length > 0
      ? { userId: userDoc._id, restaurantIds: restaurantIdsForRatings }
      : "skip"
  ) || {};

  // Query for available passes (explore tab + gift icon on list rows when enabled)
  const availablePasses = useQuery(
    showDisgoPasses && allRestaurantIds && allRestaurantIds.length > 0
      ? api.getAvailablePasses.getAvailablePassesForRestaurants
      : "skip",
    showDisgoPasses && allRestaurantIds && allRestaurantIds.length > 0
      ? {
        restaurantIds: allRestaurantIds,
        userLat: currentLocation?.latitude,
        userLng: currentLocation?.longitude,
      }
      : "skip"
  ) || [];

  const userPassClaims = useQuery(
    userDoc?._id && showDisgoPasses
      ? api.getAvailablePasses.getUserPassClaimsForCurrentWeekAll
      : "skip",
    userDoc?._id && showDisgoPasses ? { userId: userDoc._id } : "skip"
  );

  const userPointsForRestaurants = useQuery(
    userDoc?._id && showDisgoPasses
      ? api.users.getUserPointsForAllRestaurants
      : "skip",
    userDoc?._id && showDisgoPasses ? { userId: userDoc._id } : "skip"
  );

  const passesAvailableNowCount = useMemo(() => {
    if (!showDisgoPasses || !availablePasses?.length || userPassClaims === undefined) return 0;
    const cat = filterUnusedPasses(availablePasses, userPassClaims, {
      restaurants: allRestaurants,
      userMemberships,
      userPointsForRestaurants,
      selectedDay: "All",
      userId: userDoc?._id,
    });
    const combined = [
      ...(cat.down || []),
      ...(cat.location || []),
    ].filter((p) => p.isCurrentlyValid);
    return combined.length;
  }, [
    showDisgoPasses,
    availablePasses,
    userPassClaims,
    allRestaurants,
    userMemberships,
    userPointsForRestaurants,
    userDoc?._id,
  ]);

  const categorizedPasses = useUnusedPasses(availablePasses, userPassClaims, {
    restaurants: allRestaurants,
    userMemberships,
    userPointsForRestaurants,
    selectedDay,
    userId: userDoc?._id,
  });

  const accessiblePasses = [
    ...(categorizedPasses.down || []).map((pass) => ({ ...pass, category: "down" })),
    ...(categorizedPasses.location || []).map((pass) => ({ ...pass, category: "location" })),
  ];

  /** Live passes only (aligned with native MapScreen passes sheet). */
  const displayedPasses = useMemo(() => {
    if (!availablePasses?.length || userPassClaims === undefined) return [];
    const cat = filterUnusedPasses(availablePasses, userPassClaims, {
      restaurants: allRestaurants,
      userMemberships,
      userPointsForRestaurants,
      selectedDay: "All",
      userId: userDoc?._id,
    });
    const merged = [
      ...(cat.down || []).map((pass) => ({ ...pass, category: "down" })),
      ...(cat.location || []).map((pass) => ({ ...pass, category: "location" })),
    ];
    return merged.filter((p) => p.isCurrentlyValid);
  }, [
    availablePasses,
    userPassClaims,
    allRestaurants,
    userMemberships,
    userPointsForRestaurants,
    userDoc?._id,
  ]);

  const passesPrefetchActive =
    showDisgoPasses &&
    ((isMobile && showPassesMobileSheet) || (!isMobile && sideSheetActiveTab === "passes"));

  // Get restaurantId from URL parameters (supports both 'restaurant' and 'restaurantId')
  const [urlRestaurantId, setUrlRestaurantId] = useState(null);
  const [urlRestaurantLoaded, setUrlRestaurantLoaded] = useState(false);

  // Read restaurantId from URL on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Support both 'restaurant' (for QR codes) and 'restaurantId' (for backward compatibility)
    const restaurantId = urlParams.get('restaurant') || urlParams.get('restaurantId');
    if (restaurantId) {
      setUrlRestaurantId(restaurantId);
      setUrlRestaurantLoaded(false); // Reset when URL changes
    }
  }, []);

  // Query for pending restaurant data (when a Google Place is saved and selected)
  const pendingRestaurantData = useQuery(
    pendingRestaurantId ? api.getRestaurants.getRestaurantById : "skip",
    pendingRestaurantId ? { restaurantId: pendingRestaurantId } : "skip"
  );

  // Query for restaurant from URL parameter
  const urlRestaurantData = useQuery(
    urlRestaurantId && !urlRestaurantLoaded ? api.getRestaurants.getRestaurantById : "skip",
    urlRestaurantId && !urlRestaurantLoaded ? { restaurantId: urlRestaurantId } : "skip"
  );

  // Update selected restaurant when pending restaurant data loads
  useEffect(() => {
    if (pendingRestaurantData && pendingRestaurantId) {
      // Update the selected restaurant with the full database data
      setSelectedRestaurant((prev) => {
        const restaurantWithoutDeprecatedFields =
          omitDeprecatedRestaurantPlaceFields(pendingRestaurantData);
        return {
          ...prev,
          ...restaurantWithoutDeprecatedFields,
          _id: pendingRestaurantData._id,
          isGooglePlace: false,
          distance: prev?.distance, // Keep the distance from the original place
          googlePlaceId:
            pendingRestaurantData.googlePlaceId || prev?.googlePlaceId,
        };
      });

      // Clear the pending restaurant ID
      setPendingRestaurantId(null);
    }
  }, [pendingRestaurantData, pendingRestaurantId]);

  // Handle restaurant from URL parameter (only load once, allow user to close)
  useEffect(() => {
    if (urlRestaurantData && urlRestaurantId && !urlRestaurantLoaded && !selectedRestaurant) {
      // Set the restaurant as selected
      setSelectedRestaurant(omitDeprecatedRestaurantPlaceFields(urlRestaurantData));
      setUrlRestaurantLoaded(true); // Mark as loaded so it doesn't reload

      // Center map on restaurant
      if (mapRef.current && urlRestaurantData.latitude && urlRestaurantData.longitude) {
        mapRef.current.panTo({
          lat: urlRestaurantData.latitude - 0.004,
          lng: urlRestaurantData.longitude,
        });
        mapRef.current.setZoom(15);
      }

      // Hide restaurant list when showing selected restaurant
      setShowRestaurantList(false);
      setShowPassesMobileSheet(false);
    }
  }, [urlRestaurantData, urlRestaurantId, urlRestaurantLoaded, selectedRestaurant]);

  // Query for SpeedRailz restaurants
  const speedRailzRestaurantsResult = useQuery(api.getRestaurants.getSpeedRailzRestaurants);
  const speedRailzRestaurants = speedRailzRestaurantsResult || [];
  const heavenHillsRestaurantsResult = useQuery(api.getRestaurants.getHeavenHillsRestaurants);
  const heavenHillsRestaurants = heavenHillsRestaurantsResult || [];

  // Query for SpeedRailz leaderboard
  const speedRailzLeaderboardResult = useQuery(api.getRestaurants.getSpeedRailzLeaderboard);
  const speedRailzLeaderboard = speedRailzLeaderboardResult || [];

  // Helper: giftable passes for a venue (unused / down+location — same as native)
  const hasGiftablePassesForRestaurant = React.useCallback(
    (restaurantId) => {
      if (!accessiblePasses || !restaurantId) return false;
      const passAppliesToVenue = (pass) => {
        if (pass.eligibleRestaurantIds?.length) {
          return pass.eligibleRestaurantIds.some(
            (id) => String(id) === String(restaurantId)
          );
        }
        return String(pass.restaurant) === String(restaurantId);
      };
      return accessiblePasses.some((pass) => passAppliesToVenue(pass));
    },
    [accessiblePasses]
  );

  // Helper function to get friends rating badge
  const getFriendsRatingBadge = (averageRating) => {
    if (!averageRating) return null;
    if (averageRating >= 4.1) {
      return { emoji: "🤤", label: "Crave" };
    } else if (averageRating >= 3.0) {
      return { emoji: "😌", label: "Solid" };
    } else {
      return { emoji: "🫢", label: "Toss" };
    }
  };

  // Membership helper functions
  const doesUserHaveMembership = (name) => {
    if (!userMemberships) return false;
    return (
      userMemberships.filter((membership) => membership.restaurant === name)
        .length > 0
    );
  };

  const getUserMembership = (name) => {
    return userMemberships?.filter(
      (membership) => membership.restaurant === name,
    )[0];
  };

  const getCurrentMembershipTier = (name) => {
    const userMembership = getUserMembership(name);
    if (!userMembership) return null;
    const thisRestaurant = allRestaurants.find((r) => r.name === name);
    return thisRestaurant?.membershipTiers?.find(
      (tier) => tier.tier === userMembership.tier,
    );
  };

  const getNextMembershipTier = (name) => {
    const currentTier = getCurrentMembershipTier(name);
    if (!currentTier) return null;
    const thisRestaurant = allRestaurants.find((r) => r.name === name);
    return thisRestaurant?.membershipTiers?.find(
      (tier) => tier.index === currentTier.index + 1,
    );
  };

  const handlePassClaim = async (pass, isWithinTimeframe) => {
    if (!userDoc?._id) {
      Alert.alert("Error", "Please sign in to use passes.");
      return;
    }
    if (isWithinTimeframe) {
      setSelectedPass(pass);
      setShowUsePassModal(true);
      return;
    }
    try {
      const result = await claimPass({
        passId: pass._id,
        userId: userDoc._id,
        actionType: "down",
      });
      if (result.success) {
        Alert.alert("Success", `You're down for ${pass.title}!`);
      } else {
        Alert.alert("Error", result.error || "Failed to go down for pass");
      }
    } catch (error) {
      console.error("Error going down for pass:", error);
      Alert.alert("Error", "Failed to go down for pass");
    }
  };

  const handleUsePass = (pass) => {
    setSelectedPass(pass);
    setShowUsePassModal(true);
  };

  const handleCloseUsePassModal = () => {
    setShowUsePassModal(false);
    setSelectedPass(null);
  };

  const handleCantGo = async (pass) => {
    if (!userDoc?._id) {
      Alert.alert("Error", "Please sign in to use passes.");
      return;
    }
    try {
      const result = await cantGo({
        passId: pass._id,
        userId: userDoc._id,
      });
      if (result.success) {
        Alert.alert("Success", result.message || "Pass reservation cancelled.");
      } else {
        Alert.alert("Error", result.error || "Failed to cancel pass");
      }
    } catch (error) {
      console.error("Error cancelling pass:", error);
      Alert.alert("Error", "Failed to cancel pass");
    }
  };

  const handleGiftPass = async (pass, restaurantName) => {
    if (!userDoc?._id) {
      Alert.alert("Error", "Please sign in to gift passes.");
      return;
    }
    if (Platform.OS === "web") {
      setShowDownloadAppModal(true);
      return;
    }
    setSelectedPassForGift(pass);
    setSelectedRestaurantForGift({ name: restaurantName });
    setIsGiftPassWorkflowVisible(true);
  };

  const handleCloseGiftPassWorkflow = () => {
    setIsGiftPassWorkflowVisible(false);
    setSelectedPassForGift(null);
    setSelectedRestaurantForGift(null);
  };

  const handleRestaurantInfoPress = (pass) => {
    if (pass.restaurantDetails) {
      setIsLoadingRestaurant(true);
      setSelectedRestaurant(pass.restaurantDetails);
      if (
        mapRef.current &&
        pass.restaurantDetails.latitude != null &&
        pass.restaurantDetails.longitude != null
      ) {
        mapRef.current.panTo({
          lat: pass.restaurantDetails.latitude - MAP_FOCUS_LATITUDE_OFFSET,
          lng: pass.restaurantDetails.longitude,
        });
        mapRef.current.setZoom(15);
      }
      setShowRestaurantList(false);
      setShowPassesMobileSheet(false);
      setTimeout(() => {
        setIsLoadingRestaurant(false);
      }, 500);
    }
  };

  const saveGooglePlace = useMutation(api.saveGooglePlace.saveGooglePlace);
  const addRestaurant = useMutation(api.addRestaurant.addRestaurant);

  // Handle directions to restaurant
  const handleDirections = (restaurant) => {
    // If no restaurant passed, use selectedRestaurant
    const targetRestaurant = restaurant || selectedRestaurant;
    if (!targetRestaurant) return;
    const { latitude, longitude, name } = targetRestaurant;
    // For web, open Google Maps in a new tab
    const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&destination_place_id=${encodeURIComponent(name)}`;
    Linking.openURL(url);
  };

  // Helper function to request location permission
  const requestLocationForCheckIn = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        console.error("Geolocation not supported by browser");
        reject(new Error("Geolocation not supported"));
        return;
      }

      console.log("Requesting location permission...");
      getCurrentPosition(
        (position) => {
          console.log("Location obtained:", position.coords);
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          console.log("Setting currentLocation to:", location);
          setCurrentLocation(location);
          resolve(location);
        },
        (error) => {
          console.error("Error getting location:", error);
          let errorMessage = "Unable to get your location.";
          if (error.code === error.PERMISSION_DENIED) {
            errorMessage = "Location permission denied. Please enable location access in your browser settings.";
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errorMessage = "Location information is unavailable.";
          } else if (error.code === error.TIMEOUT) {
            errorMessage = "Location request timed out. Please try again.";
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    });
  };

  // Handle check-in (internal function that processes check-in with a location)
  const processCheckIn = async (restaurantId, locationToUse) => {

    try {
      // Resolve restaurant, then route to BeReal (on-site) or recap-only (remote) check-in.
      // Try to find restaurant in selectedRestaurant first, then in restaurants, then in allRestaurants
      let restaurant = selectedRestaurant?._id === restaurantId ? selectedRestaurant : null;
      if (!restaurant) {
        restaurant = restaurants?.find(r => r._id === restaurantId);
      }
      if (!restaurant) {
        restaurant = allRestaurants?.find(r => r._id === restaurantId);
      }

      // If still not found, query directly from database using Convex client
      if (!restaurant) {
        console.log("Restaurant not found in local arrays, querying from database. restaurantId:", restaurantId);
        try {
          const restaurantData = await convex.query(api.getRestaurants.getRestaurantById, { restaurantId });
          if (restaurantData) {
            restaurant = restaurantData;
            console.log("Found restaurant from database:", restaurant.name);
            // Update selectedRestaurant with the full data if it matches
            if (selectedRestaurant?._id === restaurantId) {
              setSelectedRestaurant(restaurant);
            }
          }
        } catch (dbError) {
          console.error("Error querying restaurant from database:", dbError);
        }
      }

      // If selectedRestaurant exists but is missing required fields, try to get full data
      if (restaurant && (!restaurant.latitude || !restaurant.longitude) && restaurant._id) {
        console.log("Restaurant found but missing coordinates, fetching full data. restaurantId:", restaurant._id);
        try {
          const fullRestaurantData = await convex.query(api.getRestaurants.getRestaurantById, { restaurantId: restaurant._id });
          if (fullRestaurantData && fullRestaurantData.latitude && fullRestaurantData.longitude) {
            restaurant = fullRestaurantData;
            console.log("Found full restaurant data from database:", restaurant.name);
            // Update selectedRestaurant with the full data
            if (selectedRestaurant?._id === restaurant._id) {
              setSelectedRestaurant(restaurant);
            }
          }
        } catch (dbError) {
          console.error("Error fetching full restaurant data:", dbError);
        }
      }

      if (!restaurant) {
        console.error("Restaurant not found in any array or database. restaurantId:", restaurantId);
        Alert.alert("Error", "Restaurant not found.");
        return;
      }

      console.log("Found restaurant:", restaurant.name);
      console.log("Restaurant location:", { lat: restaurant.latitude, lng: restaurant.longitude });
      console.log("User location:", locationToUse);

      // Check if restaurant has valid coordinates
      if (!restaurant.latitude || !restaurant.longitude) {
        console.error("Restaurant missing coordinates:", restaurant);
        Alert.alert("Error", "Restaurant location information is missing.");
        return;
      }

      // Distance decides BeReal (on-site) vs recap-only (remote) check-in.
      let distanceMeters = Infinity;
      if (locationToUse && restaurant.latitude != null && restaurant.longitude != null) {
        try {
          const distanceKm = calculateDistance(
            locationToUse.latitude,
            locationToUse.longitude,
            restaurant.latitude,
            restaurant.longitude
          );
          distanceMeters = distanceKm * 1000;
          console.log("Distance to restaurant:", distanceMeters, "meters");
        } catch (error) {
          console.error("Error calculating distance:", error);
        }
      }

      const alreadyCheckedInToday = await convex.query(
        api.checkInWithLocation.hasCheckedInToday,
        { userId: userDoc._id, restaurantId: restaurant._id }
      );
      if (alreadyCheckedInToday === true) {
        setAlreadyCheckedInRestaurant(restaurant);
        setShowAlreadyCheckedInModal(true);
        return;
      }

      const useBeRealFlow =
        locationToUse && distanceMeters <= CHECKIN_DISTANCE_METERS;

      if (!useBeRealFlow) {
        const latitude = locationToUse?.latitude ?? restaurant.latitude;
        const longitude = locationToUse?.longitude ?? restaurant.longitude;
        if (latitude == null || longitude == null) {
          Alert.alert("Error", "Restaurant location information is missing.");
          return;
        }

        const result = await completeRemoteRecapCheckIn({
          convex,
          checkInWithLocation,
          createMoment,
          userId: userDoc._id,
          userPrivyDid: authData?.privyDid,
          restaurantId: restaurant._id,
          latitude,
          longitude,
        });

        if (!result.ok) {
          Alert.alert(
            "Check-in Failed",
            result.message || "Unable to check in. Please try again."
          );
          return;
        }

        if (result.momentId) {
          setRecapRateMomentId(result.momentId);
          if (!isMobile) {
            setShowDesktopSideSheet(true);
            setSideSheetActiveTab("recap");
          } else {
            setShowRecapModal(true);
          }
        } else {
          Alert.alert(
            "Checked In",
            `You're checked in at ${restaurant.name || "this restaurant"}!`
          );
        }
        return;
      }

      // Within range: BeReal photo flow; DB check-in runs when the user finishes sharing.
      setRestaurantForRecap(restaurant);
      setRestaurantForBeRealCheckIn(restaurant);
      setBeRealCheckInLocationSnapshot({
        latitude: locationToUse.latitude,
        longitude: locationToUse.longitude,
      });
      if (!isMobile) {
        setShowDesktopSideSheet(true);
      }
      setShowBeRealCheckIn(true);
      setCheckInRestaurantId(restaurant._id);
    } catch (error) {
      console.error("Error during check-in:", error);
      const restaurant = restaurants?.find(r => r._id === restaurantId);
      Alert.alert(
        "Hmm, Something's Not Right 🤔",
        `We couldn't complete your check-in at ${restaurant?.name || 'this restaurant'}.\n\nPlease try again in a moment.`,
        [{
          text: "Try Again",
          style: "default"
        }]
      );
    }
  };

  // Handle check-in (public function that handles location prompt)
  const handleCheckIn = async (restaurantId) => {
    if (!userDoc?._id) {
      Alert.alert("Error", "Please sign in to check in.");
      return;
    }

    // Request location when available; remote recap check-in still works without GPS.
    let locationToUse = currentLocation;
    if (!locationToUse) {
      try {
        console.log("Location not available, requesting location...");
        locationToUse = await requestLocationForCheckIn();
        console.log("Location received in handleCheckIn:", locationToUse);
      } catch (error) {
        console.warn("Location unavailable — using remote recap check-in:", error);
        locationToUse = null;
      }
    }

    processCheckIn(restaurantId, locationToUse);
  };

  // Handle claim membership
  const handleClaimMembership = () => {
    if (!selectedRestaurant) return;
    createMembership({
      privyDid: authData.privyDid,
      restaurantName: selectedRestaurant.name,
      imgUrl: selectedRestaurant.imgUrl,
    }).then(() => {
      Alert.alert(
        "Success",
        `You have successfully claimed a membership at ${selectedRestaurant.name}`,
      );
    });
  };

  // Request user location (only when explicitly requested, not on page load)
  const requestUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setCurrentLocation(location);
          setInitialRegion({
            ...location,
            latitudeDelta: 0.02,
            longitudeDelta: 0.02,
          });
          // Center map on user location after getting it (matching mobile app)
          if (mapRef.current) {
            mapRef.current.panTo({
              lat: location.latitude - 0.004,
              lng: location.longitude,
            });
            mapRef.current.setZoom(15);
          }
        },
        (error) => {
          console.log("Error getting location:", error);
          Alert.alert(
            "Location Access",
            "Unable to get your location. Please enable location permissions in your browser settings.",
            [{ text: "OK" }]
          );
        }
      );
    } else {
      Alert.alert(
        "Location Not Supported",
        "Your browser does not support geolocation.",
        [{ text: "OK" }]
      );
    }
  }, []);

  // Handle center map - centers on user location (matching mobile app behavior)
  const handleCenterMap = useCallback(() => {
    if (!currentLocation) {
      // Request location if not available
      requestUserLocation();
      return;
    }

    // Directly center the map using the map ref (matching mobile app pattern)
    if (mapRef.current) {
      // Match mobile app's offset: latitude - 0.004 for better view
      mapRef.current.panTo({
        lat: currentLocation.latitude - 0.004,
        lng: currentLocation.longitude,
      });
      mapRef.current.setZoom(15);
    }
  }, [currentLocation, requestUserLocation]);

  // Handle center map on restaurant - centers on selected restaurant location
  const handleCenterMapOnRestaurant = useCallback((restaurant) => {
    if (!restaurant || restaurant.latitude == null || restaurant.longitude == null) {
      return;
    }

    if (mapRef.current) {
      mapRef.current.panTo({
        lat: restaurant.latitude - MAP_FOCUS_LATITUDE_OFFSET,
        lng: restaurant.longitude,
      });
      mapRef.current.setZoom(15);
    }
  }, []);

  // Handle benefit press
  const handleBenefitPress = (benefit) => {
    Alert.alert(
      String(benefit.benefit),
      `Would you like to use this benefit?`,
      [
        {
          text: "Cancel",
          onPress: () => { },
          style: "cancel",
        },
        {
          text: "Use",
          onPress: () => {
            // Handle benefit use
          },
        },
      ],
      { cancelable: false },
    );
  };

  // Handle gift perk
  const handleGiftPerk = async () => {
    try {
      if (!selectedRestaurant?.isPartner) {
        Alert.alert("Error", "This feature is only available for Disgo partner restaurants.");
        return;
      }

      const membership = getUserMembership(selectedRestaurant.name);
      if (!membership) {
        Alert.alert("Error", "You need a membership to gift perks.");
        return;
      }

      // For web, show gift flow
      Alert.alert("Gift Perk", "Gift perk functionality coming soon!");
    } catch (error) {
      console.error("Error fetching benefits:", error);
      Alert.alert("Error", "Unable to fetch benefits.");
    }
  };

  // Handle close bottom sheet
  const handleCloseBottomSheet = () => {
    setSelectedRestaurant(null);
    setShowPassesMobileSheet(false);
    setShowRestaurantList(true);
  };

  // Handle missing perk (placeholder)
  const handleMissingPerk = () => {
    // Placeholder for missing perk handling
  };

  // Helper function to convert degrees to radians
  const deg2rad = (deg) => {
    return (deg * Math.PI) / 180;
  };

  // Helper function to calculate distance between two points (in km)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  };

  // Day abbreviations for day chip row in explore list.
  const getDayAbbreviation = (day) => {
    const abbreviations = {
      All: "All",
      Monday: "Mon",
      Tuesday: "Tues",
      Wednesday: "Wed",
      Thursday: "Thurs",
      Friday: "Fri",
      Saturday: "Sat",
      Sunday: "Sun",
    };
    return abbreviations[day] || day;
  };

  // Filter restaurants (aligned with MapScreen.js: day, friend-rating segment, sheet toggles)
  const filteredRestaurants = useMemo(() => {
    // For Daily Specials and Disgo Passes tabs, prefer allRestaurants (unfiltered)
    // But if allRestaurants has fewer items than restaurants, use restaurants instead
    // This ensures we show all available restaurants even if pagination hasn't loaded all yet
    // For map display, use restaurants (search-filtered)
    let baseRestaurants;
    if (allRestaurants && allRestaurants.length >= (restaurants?.length || 0)) {
      baseRestaurants = allRestaurants;
    } else {
      baseRestaurants = restaurants;
    }

    if (!baseRestaurants || baseRestaurants.length === 0) {
      return [];
    }

    // If filtering by day and specials haven't loaded yet, block rendering
    if (selectedDay !== "All" && !dailySpecials) {
      return [];
    }

    // Start with base restaurants
    let filtered = baseRestaurants;

    // IMPORTANT: Client-side bounds filtering to ensure list always matches visible map
    // This is a backup in case backend queries don't refresh immediately
    // Only filter by bounds when NOT searching (search should show all results)
    if (mapBounds && !searchQuery.trim()) {
      filtered = filtered.filter((restaurant) => {
        if (!restaurant.latitude || !restaurant.longitude) return false;

        const { northEast, southWest } = mapBounds;
        const inBounds = (
          restaurant.latitude >= southWest.lat &&
          restaurant.latitude <= northEast.lat &&
          restaurant.longitude >= southWest.lng &&
          restaurant.longitude <= northEast.lng
        );

        return inBounds;
      });
    }

    // Filter by day of week
    if (selectedDay !== "All" && dailySpecials) {
      const restaurantIdsWithSpecials = new Set(
        dailySpecials
          .filter((special) => special.dayOfWeek === selectedDay)
          .map((special) => special.restaurantId)
      );

      filtered = filtered.filter((restaurant) =>
        restaurantIdsWithSpecials.has(restaurant._id)
      );
    }

    // Filter to only SpeedRailz restaurants if toggle is enabled
    if (showOnlySpeedRailzOnMap && speedRailzRestaurants && speedRailzRestaurants.length > 0) {
      const speedRailzIds = new Set(speedRailzRestaurants.map((r) => r._id));
      filtered = filtered.filter((restaurant) => speedRailzIds.has(restaurant._id));
    }

    if (showOnlyHeavenHillsOnMap && heavenHillsRestaurants && heavenHillsRestaurants.length > 0) {
      const heavenHillsIds = new Set(heavenHillsRestaurants.map((r) => r._id));
      filtered = filtered.filter((restaurant) => heavenHillsIds.has(restaurant._id));
    }

    if (showDisgoRestaurants || showNonDisgoRestaurants) {
      filtered = filtered.filter((restaurant) => {
        const isDisgo = restaurant.isPartner || false;
        if (showDisgoRestaurants && isDisgo) return true;
        if (showNonDisgoRestaurants && !isDisgo) return true;
        return false;
      });
    }

    if (showFriendsRatedRestaurants && friendsAverageRatings) {
      filtered = filtered.filter((restaurant) => {
        const ratingData = friendsAverageRatings[restaurant._id];
        return ratingData && ratingData.ratingCount > 0;
      });
    }

    if (showMyCheckedInRestaurants) {
      const currentCheckinHistory = userCheckinHistoryQuery;
      if (currentCheckinHistory === undefined || currentCheckinHistory === null) {
        // Still loading — show all until data resolves (matches native)
      } else if (Array.isArray(currentCheckinHistory)) {
        if (currentCheckinHistory.length > 0) {
          const checkedInIds = new Set(currentCheckinHistory.map((id) => String(id)));
          filtered = filtered.filter((restaurant) => {
            if (!restaurant || !restaurant._id) return false;
            return checkedInIds.has(String(restaurant._id));
          });
        } else {
          filtered = [];
        }
      }
    }

    // Calculate distances if we have current location
    if (currentLocation) {
      filtered = filtered.map((restaurant) => ({
        ...restaurant,
        distance: getDistance(
          currentLocation.latitude,
          currentLocation.longitude,
          restaurant.latitude,
          restaurant.longitude
        )
      }));
    }

    // Sort by partner status first, then by distance if available
    const sorted = filtered.sort((a, b) => {
      // First sort by partner status
      if (a.isPartner && !b.isPartner) return -1;
      if (!a.isPartner && b.isPartner) return 1;

      // Then sort by distance if we have current location
      if (currentLocation) {
        return a.distance - b.distance;
      }

      // If no location, maintain original order within partner groups
      return 0;
    });

    return sorted;
  }, [
    restaurants,
    allRestaurants,
    searchQuery,
    selectedDay,
    dailySpecials,
    currentLocation,
    friendsAverageRatings,
    showOnlySpeedRailzOnMap,
    speedRailzRestaurants,
    showOnlyHeavenHillsOnMap,
    heavenHillsRestaurants,
    showDisgoRestaurants,
    showNonDisgoRestaurants,
    showFriendsRatedRestaurants,
    showMyCheckedInRestaurants,
    userCheckinHistoryQuery,
    mapBounds,
  ]);

  useEffect(() => {
    if (
      showFriendsRatedRestaurants &&
      filteredRestaurants &&
      filteredRestaurants.length < 20 &&
      allRestaurantsResult?.status === "CanLoadMore" &&
      !allRestaurantsResult?.isLoading
    ) {
      allRestaurantsResult.loadMore(100);
    }
  }, [filteredRestaurants, showFriendsRatedRestaurants, allRestaurantsResult]);

  /** Disgo Passes tab body — same cards as native MapScreen. */
  const renderDisgoPassesExploreList = () => {
    if (!displayedPasses?.length) {
      return (
        <View style={{ alignItems: "center", paddingTop: 4, paddingHorizontal: 16, paddingBottom: 24 }}>
          <Text style={{ fontSize: 18, fontFamily: fontFamily.abhayaBold, color: colors.gray500 }}>
            Nothing available to redeem right now
          </Text>
          <Text
            style={{
              fontSize: 14,
              fontFamily: fontFamily.abhaya,
              color: colors.gray400,
              marginTop: 8,
              textAlign: "center",
            }}
          >
            Check back when you’re in a pass’s time window, or open a restaurant for more perks.
          </Text>
        </View>
      );
    }
    return displayedPasses.map((item) => {
      const distanceKm =
        item.restaurantDetails && currentLocation
          ? calculateDistance(
              currentLocation.latitude,
              currentLocation.longitude,
              item.restaurantDetails.latitude,
              item.restaurantDetails.longitude
            )
          : null;
      const distanceText = distanceKm != null ? formatDistanceDisplay(distanceKm) : null;
      return (
        <View key={`pass-${item._id}`} style={{ marginHorizontal: 16, marginBottom: 8 }}>
          <PassClaimStatusWrapper pass={item} userDoc={userDoc}>
            {(isClaimedThisWeek, actionType) => (
              <PassCard
                pass={item}
                showDistance={true}
                distance={distanceText}
                showRestaurantName={true}
                restaurantName={getPassVenueNamesLine(item) || item.restaurantDetails?.name}
                showClaimStatus={true}
                isClaimedThisWeek={isClaimedThisWeek}
                actionType={actionType}
                showActionButtons={true}
                onGiftPress={handleGiftPass}
                onClaimPress={handlePassClaim}
                onUsePassPress={handleUsePass}
                onCantGoPress={handleCantGo}
                isDisgoPass={true}
                onRestaurantInfoPress={handleRestaurantInfoPress}
              />
            )}
          </PassClaimStatusWrapper>
        </View>
      );
    });
  };

  // Ref for debouncing region changes
  const regionChangeTimeoutRef = useRef(null);
  const [hasUserInteractedWithMap, setHasUserInteractedWithMap] = useState(false);

  // Handle map region changes to update bounds
  const handleMapRegionChange = (region, immediate = false) => {
    // Clear any pending timeout
    if (regionChangeTimeoutRef.current) {
      clearTimeout(regionChangeTimeoutRef.current);
    }

    const updateBounds = () => {
      // Always create a new bounds object to ensure React/Convex detects the change
      // This is critical for usePaginatedQuery to re-run when bounds change
      const bounds = {
        northEast: {
          lat: region.latitude + region.latitudeDelta / 2,
          lng: region.longitude + region.longitudeDelta / 2,
        },
        southWest: {
          lat: region.latitude - region.latitudeDelta / 2,
          lng: region.longitude - region.longitudeDelta / 2,
        },
      };

      // Always set new bounds object to ensure Convex detects the change
      // Don't optimize by returning same reference - we need queries to refresh
      setMapBounds(bounds);
      setHasUserInteractedWithMap(true);
    };

    // If immediate (e.g., from programmatic animation), update right away
    // Otherwise debounce to avoid excessive updates during user panning
    if (immediate) {
      updateBounds();
    } else {
      regionChangeTimeoutRef.current = setTimeout(updateBounds, 300);
    }
  };

  // Cleanup region change timeout on unmount
  useEffect(() => {
    return () => {
      if (regionChangeTimeoutRef.current) {
        clearTimeout(regionChangeTimeoutRef.current);
      }
    };
  }, []);

  // Handle marker press
  const handleMarkerPress = React.useCallback(
    (restaurant) => {
      setSelectedRestaurant(restaurant);
      // On desktop, show in side sheet; on mobile, hide restaurant list
      if (!isMobile) {
        setShowDesktopSideSheet(true);
        if (sideSheetActiveTab !== "restaurant") {
          setPreviousTab(sideSheetActiveTab);
        }
        setSideSheetActiveTab("restaurant");
      } else {
        setShowRestaurantList(false);
        setShowPassesMobileSheet(false);
      }
      if (
        mapRef.current &&
        restaurant?.latitude != null &&
        restaurant?.longitude != null
      ) {
        mapRef.current.panTo({
          lat: restaurant.latitude - MAP_FOCUS_LATITUDE_OFFSET,
          lng: restaurant.longitude,
        });
        mapRef.current.setZoom(15);
      }
    },
    [isMobile, sideSheetActiveTab]
  );

  // Handle restaurant press from list
  const handleRestaurantPress = (restaurant) => {
    setSelectedRestaurant(restaurant);
    // On desktop, show in side sheet; on mobile, close list
    if (!isMobile) {
      setShowDesktopSideSheet(true);
      if (sideSheetActiveTab !== "restaurant") {
        setPreviousTab(sideSheetActiveTab);
      }
      setSideSheetActiveTab("restaurant");
    } else {
      setShowRestaurantList(false);
      setShowPassesMobileSheet(false);
    }
    if (
      mapRef.current &&
      restaurant?.latitude != null &&
      restaurant?.longitude != null
    ) {
      mapRef.current.panTo({
        lat: restaurant.latitude - MAP_FOCUS_LATITUDE_OFFSET,
        lng: restaurant.longitude,
      });
      mapRef.current.setZoom(15);
    }
  };

  // Handle loading more restaurants (explore list is always restaurant browse)
  const handleLoadMore = () => {
    if (allRestaurantsResult?.status === "CanLoadMore") {
      allRestaurantsResult.loadMore(100);
    }
    if (restaurantsResult?.status === "CanLoadMore") {
      restaurantsResult.loadMore(100);
    }
  };

  /**
   * Passes query is limited to paginated restaurants in the current map bounds (100 per page).
   * Pull extra pages while the passes sheet / desktop passes pane is open.
   */
  const allRestaurantsLenPassesPrefetch =
    passesPrefetchActive ? allRestaurantsResult?.results?.length ?? 0 : 0;
  useEffect(() => {
    if (!passesPrefetchActive) return;
    if (!allRestaurantsResult || allRestaurantsResult.status !== "CanLoadMore") return;
    if (allRestaurantsResult.isLoading) return;
    if ((allRestaurantsResult.results?.length ?? 0) >= 5000) return;
    allRestaurantsResult.loadMore(100);
  }, [
    passesPrefetchActive,
    showDisgoPasses,
    allRestaurantsResult?.status,
    allRestaurantsResult?.isLoading,
    allRestaurantsLenPassesPrefetch,
  ]);

  // Handle search query changes
  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);

    // If query is empty, clear Google Places results
    if (!query.trim()) {
      clearGooglePlacesSearch();
      return;
    }

    // Search Google Places if query is long enough (minimum 2 characters)
    if (query.trim().length >= 2) {
      searchGooglePlaces(query, {
        location: currentLocation,
        debounce: true,
        debounceMs: 200
      });
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    clearGooglePlacesSearch();
  };

  // Focus the always-visible search field and expand the list sheet
  const handleOpenSearch = () => {
    setShowPassesMobileSheet(false);
    setShowRestaurantList(true);
    setListSheetHeight(92);
    setIsListSheetExpanded(true);
    setShowDesktopSideSheet(true);
    setSideSheetActiveTab("main");
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  // Handle Google Places result selection
  const handleGooglePlaceSelect = async (place, savedRestaurantId = null) => {
    // Keep search query/results so closing restaurant detail returns to the same search
    // Show loading animation
    setIsLoadingRestaurant(true);

    let selectedRestaurantData;

    if (place.isDisgoRestaurant) {
      // For Disgo restaurants, use the full restaurant data to preserve all partner information
      selectedRestaurantData = {
        ...place,
        _id: place.id, // Ensure _id is set correctly
        isGooglePlace: false
      };
    } else {
      // For Google Places that were just saved, fetch the actual Disgo restaurant data directly from API
      if (!savedRestaurantId) {
        console.error('No savedRestaurantId provided for Google Place selection');
        setIsLoadingRestaurant(false);
        return;
      }

      // Create a basic restaurant object with the saved ID
      // The actual restaurant data will be loaded by the pendingRestaurantData query
      selectedRestaurantData = {
        _id: savedRestaurantId,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        googlePlaceId: place.id,
        cuisine: '', // Will be loaded from database
        price: place.priceLevel ? '$'.repeat(place.priceLevel) : '',
        rating: place.rating || 0,
        userRatingCount: place.userRatingCount || 0,
        isGooglePlace: false, // Now it's a Disgo restaurant
        isPartner: false, // Will be loaded from database
        distance: place.distance
      };

      // Set the pending restaurant ID to trigger the query
      setPendingRestaurantId(savedRestaurantId);
    }

    // Set as selected restaurant and close Google Places results
    setSelectedRestaurant(selectedRestaurantData);
    setShowGooglePlacesResults(false);

    if (!isMobile) {
      setShowDesktopSideSheet(true);
      if (sideSheetActiveTab !== "restaurant") {
        setPreviousTab(sideSheetActiveTab);
      }
      setSideSheetActiveTab("restaurant");
    } else {
      setShowRestaurantList(false);
      setShowPassesMobileSheet(false);
    }

    // Animate map to the selected place
    if (
      mapRef.current &&
      place?.latitude != null &&
      place?.longitude != null
    ) {
      mapRef.current.panTo({
        lat: place.latitude - MAP_FOCUS_LATITUDE_OFFSET,
        lng: place.longitude,
      });
      mapRef.current.setZoom(15);
      handleMapRegionChange({
        latitude: place.latitude - MAP_FOCUS_LATITUDE_OFFSET,
        longitude: place.longitude,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      }, true);
    }

    // Hide loading after a short delay
    setTimeout(() => {
      setIsLoadingRestaurant(false);
    }, 500);
  };

  // Handle saving Google Place to Disgo database
  const handleSaveGooglePlace = async (place, options = {}) => {
    const { silent = false } = options;
    try {
      const result = await saveGooglePlace({
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        priceLevel: place.priceLevel,
        rating: place.rating,
        userRatingCount: place.userRatingCount,
        googlePlaceId: place.id,
        types: place.types || [],
      });

      if (result.success) {
        // Add to saved places set
        setSavedGooglePlaces(prev => new Set([...prev, place.id]));

        if (!silent) {
          Alert.alert(
            "Success! 🎉",
            `${place.name} has been saved to Disgo! You can now check in and earn points here.`,
            [{ text: "Awesome!", style: "default" }]
          );
        }

        // Return the restaurant ID for use in place selection
        return result.restaurantId;
      } else {
        if (result.error.includes("already exists")) {
          // Place already exists, just mark as saved
          setSavedGooglePlaces(prev => new Set([...prev, place.id]));

          if (!silent) {
            Alert.alert(
              "Already Saved",
              `${place.name} is already in your Disgo database!`,
              [{ text: "Got it!", style: "default" }]
            );
          }

          // Return the existing restaurant ID
          return result.restaurantId;
        } else {
          if (!silent) {
            Alert.alert(
              "Error",
              `Failed to save ${place.name}: ${result.error}`,
              [{ text: "OK", style: "default" }]
            );
          }
          return null;
        }
      }
    } catch (error) {
      console.error('Error saving Google Place:', error);
      if (!silent) {
        Alert.alert(
          "Error",
          `Failed to save ${place.name}. Please try again.`,
          [{ text: "OK", style: "default" }]
        );
      }
      return null;
    }
  };

  // Auto-open list on mount (matching native behavior)
  useEffect(() => {
    setShowRestaurantList(true);
  }, []);

  return (
    <View style={[
      styles.container,
      !isMobile && styles.containerDesktop
    ]}>
      {/* Desktop: Map with buttons above and list to the right */}
      {!isMobile ? (
        <View style={styles.mapWrapperDesktop}>
          {/* Map and List side by side */}
          <View style={styles.mapAndListContainer}>
            {/* Map container */}
            <View style={styles.mapContainerDesktop}>
              <MapComponent
                restaurants={filteredRestaurants || []}
                selectedRestaurant={selectedRestaurant}
                onMarkerPress={handleMarkerPress}
                mapRef={mapRef}
                initialRegion={initialRegion}
                currentLocation={currentLocation}
                onRegionChange={handleMapRegionChange}
                userCheckinHistory={userCheckinHistory || []}
              />

              {/* Buttons: reopen side sheet and location */}
              <View style={styles.mapTopButtonsContainer}>
                {/* Location Button */}
                <Pressable
                  onPress={handleCenterMap}
                  style={styles.reopenSideSheetButton}
                >
                  <Ionicons
                    name={currentLocation ? "locate" : "locate-outline"}
                    size={24}
                    color={currentLocation ? "#4e2916" : "#999"}
                  />
                  {currentLocation && (
                    <View style={styles.locationIndicatorSmall} />
                  )}
                </Pressable>

                {/* Menu when sidebar closed; left chevron in the same spot when open (dismiss check-in first, else collapse sidebar) */}
                {!showDesktopSideSheet ? (
                  <TouchableOpacity
                    onPress={() => setShowDesktopSideSheet(true)}
                    style={styles.reopenSideSheetButton}
                    accessibilityRole="button"
                    accessibilityLabel="Open sidebar"
                  >
                    <Ionicons name="menu" size={24} color="#4e2916" />
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    onPress={() => {
                      if (showBeRealCheckIn) {
                        setShowBeRealCheckIn(false);
                        setRestaurantForBeRealCheckIn(null);
                        setBeRealCheckInLocationSnapshot(null);
                        setRestaurantForRecap(null);
                      } else {
                        setShowDesktopSideSheet(false);
                      }
                    }}
                    style={styles.reopenSideSheetButton}
                    accessibilityRole="button"
                    accessibilityLabel={showBeRealCheckIn ? "Back from check-in" : "Collapse sidebar"}
                  >
                    <Ionicons name="chevron-back" size={24} color="#4e2916" />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {/* Side Sheet - Right side for desktop */}
            {showDesktopSideSheet && (
              <View style={styles.rightSheetDesktop}>
                {showBeRealCheckIn ? (
                  <View style={styles.rightSheetCheckInColumn}>
                    <View style={styles.rightSheetCheckInHeader}>
                      <View style={styles.desktopSideSheetCloseRow}>
                        <TouchableOpacity
                          onPress={() => {
                            setShowBeRealCheckIn(false);
                            setRestaurantForBeRealCheckIn(null);
                            setBeRealCheckInLocationSnapshot(null);
                            setRestaurantForRecap(null);
                          }}
                          style={[
                            styles.sideSheetNavButton,
                            styles.sideSheetNavButtonFixed,
                            styles.sideSheetCloseButtonDistinct,
                          ]}
                          accessibilityRole="button"
                          accessibilityLabel="Close check-in"
                        >
                          <Ionicons name="close" size={20} color="#6B4C3B" />
                        </TouchableOpacity>
                      </View>
                      <Text style={styles.rightSheetCheckInTitle} numberOfLines={1}>
                        Check-in
                      </Text>
                    </View>
                    <View style={styles.rightSheetCheckInBody}>
                      <BeRealCheckIn
                        visible={showBeRealCheckIn}
                        restaurant={restaurantForBeRealCheckIn}
                        userPrivyDid={authData?.privyDid}
                        userId={userDoc?._id}
                        checkInLocation={beRealCheckInLocationSnapshot ?? undefined}
                        desktopWebOptimized
                        embeddedInSidebar
                        onComplete={() => {
                          setShowBeRealCheckIn(false);
                          setRestaurantForBeRealCheckIn(null);
                          setBeRealCheckInLocationSnapshot(null);
                          setRestaurantForRecap(null);
                          setShowDiscoModal(true);
                        }}
                        onCancel={() => {
                          setShowBeRealCheckIn(false);
                          setRestaurantForBeRealCheckIn(null);
                          setBeRealCheckInLocationSnapshot(null);
                          setRestaurantForRecap(null);
                        }}
                      />
                    </View>
                  </View>
                ) : (
                <>
                {/* Header with Nav Bar */}
                <View style={styles.listHeader}>
                  {/* Header Top Bar - Nav chips (close is on the map, same control as menu) */}
                  <View style={[styles.listHeaderTop, styles.desktopSideSheetHeaderTop]}>
                    <View style={styles.sideSheetNavBar}>
                      {sideSheetActiveTab === "restaurant" ? (
                        /* Back button when showing restaurant */
                        <TouchableOpacity
                          onPress={() => {
                            setSelectedRestaurant(null);
                            setSideSheetActiveTab(
                              previousTab && previousTab !== "restaurant" ? previousTab : "main"
                            );
                          }}
                          style={[styles.sideSheetNavButton, styles.sideSheetNavButtonFixed]}
                        >
                          <Ionicons
                            name="arrow-back"
                            size={20}
                            color="#4e2916"
                          />
                          <Text
                            style={[styles.sideSheetNavButtonLabel, styles.sideSheetNavButtonLabelActive]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            Back
                          </Text>
                        </TouchableOpacity>
                      ) : (
                        /* Regular nav buttons */
                        <>
                          <TouchableOpacity
                            onPress={() => {
                              handleClearSearch();
                              setSideSheetActiveTab("main");
                            }}
                            style={[
                              styles.sideSheetNavButton,
                              styles.sideSheetNavButtonFixed,
                              sideSheetActiveTab === "main" && !searchQuery.trim() && styles.sideSheetNavButtonActive,
                            ]}
                          >
                            <Ionicons
                              name="list"
                              size={20}
                              color={sideSheetActiveTab === "main" && !searchQuery.trim() ? "#4e2916" : "#666"}
                            />
                            <Text
                              style={[
                                styles.sideSheetNavButtonLabel,
                                sideSheetActiveTab === "main" && !searchQuery.trim() && styles.sideSheetNavButtonLabelActive,
                              ]}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              Restaurants
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => {
                              handleClearSearch();
                              setSideSheetActiveTab("filter");
                            }}
                            style={[
                              styles.sideSheetNavButton,
                              styles.sideSheetNavButtonFixed,
                              sideSheetActiveTab === "filter" && styles.sideSheetNavButtonActive,
                            ]}
                          >
                            <Ionicons
                              name="filter"
                              size={20}
                              color={sideSheetActiveTab === "filter" ? "#4e2916" : "#666"}
                            />
                            <Text
                              style={[
                                styles.sideSheetNavButtonLabel,
                                sideSheetActiveTab === "filter" && styles.sideSheetNavButtonLabelActive,
                              ]}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              Filters
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={handleOpenSearch}
                            style={[
                              styles.sideSheetNavButton,
                              styles.sideSheetNavButtonFixed,
                              searchQuery.trim() && sideSheetActiveTab === "main" && styles.sideSheetNavButtonActive,
                            ]}
                          >
                            <Ionicons
                              name="search"
                              size={20}
                              color={searchQuery.trim() && sideSheetActiveTab === "main" ? "#4e2916" : "#666"}
                            />
                            <Text
                              style={[
                                styles.sideSheetNavButtonLabel,
                                searchQuery.trim() && sideSheetActiveTab === "main" && styles.sideSheetNavButtonLabelActive,
                              ]}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              Search
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => setShowDownloadAppModal(true)}
                            style={[styles.sideSheetNavButton, styles.sideSheetNavButtonFixed]}
                            accessibilityRole="button"
                            accessibilityLabel="House Pick — get the app"
                          >
                            <Ionicons name="dice-outline" size={20} color="#666" />
                            <Text
                              style={styles.sideSheetNavButtonLabel}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              House Pick
                            </Text>
                          </TouchableOpacity>
                          {showDisgoPasses && (
                            <View style={{ position: "relative" }}>
                              <Pressable
                                onPress={() =>
                                  handleDisgoPassesButtonPress({
                                    isEnabled: showDisgoPasses,
                                    onOpenPasses: () => {
                                      setShowDesktopSideSheet(true);
                                      handleClearSearch();
                                      setSideSheetActiveTab((t) => (t === "passes" ? "main" : "passes"));
                                    },
                                  })
                                }
                                style={({ pressed }) => [
                                  styles.sideSheetNavButton,
                                  styles.sideSheetNavButtonFixed,
                                  sideSheetActiveTab === "passes" && styles.sideSheetNavButtonActive,
                                  pressed && { opacity: 0.92 },
                                ]}
                                accessibilityRole="button"
                                accessibilityLabel={
                                  passesAvailableNowCount > 0
                                    ? `Passes, ${passesAvailableNowCount} available to redeem now`
                                    : "Passes"
                                }
                              >
                                <Ionicons
                                  name="ticket-outline"
                                  size={20}
                                  color={sideSheetActiveTab === "passes" ? "#4e2916" : "#666"}
                                />
                                <Text
                                  style={[
                                    styles.sideSheetNavButtonLabel,
                                    sideSheetActiveTab === "passes" && styles.sideSheetNavButtonLabelActive,
                                  ]}
                                  numberOfLines={1}
                                  ellipsizeMode="tail"
                                >
                                  Passes
                                </Text>
                              </Pressable>
                              <PassesAvailabilityBadge count={passesAvailableNowCount} />
                            </View>
                          )}
                          {!selectedRestaurant && (
                            <TouchableOpacity
                              onPress={handleDesktopRecapTabPress}
                              style={[
                                styles.sideSheetNavButton,
                                styles.sideSheetNavButtonFixed,
                                sideSheetActiveTab === "recap" && styles.sideSheetNavButtonActive,
                              ]}
                              accessibilityRole="button"
                              accessibilityLabel="Recap"
                            >
                              <Ionicons
                                name="camera-outline"
                                size={20}
                                color={sideSheetActiveTab === "recap" ? "#4e2916" : "#666"}
                              />
                              <Text
                                style={[
                                  styles.sideSheetNavButtonLabel,
                                  sideSheetActiveTab === "recap" && styles.sideSheetNavButtonLabelActive,
                                ]}
                                numberOfLines={1}
                                ellipsizeMode="tail"
                              >
                                Recap
                              </Text>
                            </TouchableOpacity>
                          )}
                          {isHeavenHillsMapPromoUiEnabled ? (
                            <TouchableOpacity
                              onPress={() => setSideSheetActiveTab("heavenhills")}
                              style={[
                                styles.sideSheetNavButton,
                                styles.sideSheetNavButtonFixed,
                                sideSheetActiveTab === "heavenhills" && styles.sideSheetNavButtonActive,
                              ]}
                              accessibilityRole="button"
                              accessibilityLabel="Paloma Palooza"
                            >
                              <View
                                style={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 4,
                                  backgroundColor: PALOMA_NAVY,
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Image
                                  source={PP_LOGO}
                                  style={{ width: 16, height: 16 }}
                                  resizeMode="contain"
                                  accessibilityIgnoresInvertColors
                                />
                              </View>
                              <Text
                                style={[
                                  styles.sideSheetNavButtonLabel,
                                  sideSheetActiveTab === "heavenhills" && styles.sideSheetNavButtonLabelActive,
                                ]}
                                numberOfLines={1}
                                ellipsizeMode="tail"
                              >
                                Paloma Palooza
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </>
                      )}
                    </View>
                  </View>

                  {/* Content based on active tab */}
                  {sideSheetActiveTab === "main" && (
                    <>
                      <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
                        <View style={styles.searchBar}>
                          <Ionicons name="search" size={20} color="#666" />
                          <TextInput
                            ref={searchInputRef}
                            style={styles.searchInput}
                            placeholder="Search restaurants or cuisine..."
                            placeholderTextColor="#666"
                            value={searchQuery}
                            onChangeText={handleSearchQueryChange}
                          />
                          {searchQuery.length > 0 && (
                            <TouchableOpacity
                              onPress={handleClearSearch}
                              style={styles.clearButton}
                            >
                              <Ionicons name="close-circle" size={20} color="#666" />
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                      {!searchQuery.trim() && (
                        <View style={styles.dayTabsContainer}>
                          {days.map((day) => (
                            <TouchableOpacity
                              key={day}
                              onPress={() => setSelectedDay(day)}
                              style={[styles.dayTab, selectedDay === day && styles.dayTabActive]}
                            >
                              <Text
                                style={[styles.dayTabText, selectedDay === day && styles.dayTabTextActive]}
                              >
                                {getDayAbbreviation(day)}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                      <View style={styles.mainListHeaderSpacer} />
                    </>
                  )}
                  {sideSheetActiveTab === "passes" && (
                    <>
                      <Text
                        style={{
                          paddingHorizontal: 16,
                          paddingBottom: 8,
                          fontSize: 13,
                          fontFamily: "AbhayaLibre_400Regular, serif",
                          color: "#9C8A80",
                          textAlign: "center",
                        }}
                      >
                        Live passes you can use in the current time window.
                      </Text>
                      <View style={styles.mainListHeaderSpacer} />
                    </>
                  )}
                </View>

                {/* Main sheet body: Filters fill the column; other tabs scroll. Recap: list / rate / edit stay in the sidebar on desktop. */}
                {sideSheetActiveTab === "filter" ? (
                  <View style={styles.desktopFilterSheetBody}>
                    <FilterBottomSheetContent
                      showOnlySpeedRailzOnMap={showOnlySpeedRailzOnMap}
                      setShowOnlySpeedRailzOnMap={setShowOnlySpeedRailzOnMap}
                      showOnlyHeavenHillsOnMap={showOnlyHeavenHillsOnMap}
                      setShowOnlyHeavenHillsOnMap={setShowOnlyHeavenHillsOnMap}
                      showDisgoRestaurants={showDisgoRestaurants}
                      setShowDisgoRestaurants={setShowDisgoRestaurants}
                      showNonDisgoRestaurants={showNonDisgoRestaurants}
                      setShowNonDisgoRestaurants={setShowNonDisgoRestaurants}
                      showFriendsRatedRestaurants={showFriendsRatedRestaurants}
                      setShowFriendsRatedRestaurants={setShowFriendsRatedRestaurants}
                      showMyCheckedInRestaurants={showMyCheckedInRestaurants}
                      setShowMyCheckedInRestaurants={setShowMyCheckedInRestaurants}
                    />
                  </View>
                ) : sideSheetActiveTab === "recap" ? (
                  <View style={styles.desktopRecapSheetBody}>
                    {recapEditMomentId ? (
                      <CheckInScreen
                        route={{
                          key: "recap-web-checkin-sidebar",
                          params: {
                            momentId: recapEditMomentId,
                            editMoment: true,
                            embeddedWebRecap: true,
                            embeddedSidebarRecap: true,
                            onRequireAppDownload: () => {
                              setShowDownloadAppModal(true);
                            },
                          },
                        }}
                        navigation={{
                          goBack: () => setRecapEditMomentId(null),
                          setParams: () => {},
                          navigate: (screenName, params) => {
                            if (screenName === "Tabs" && params?.screen === "Social") {
                              setActiveTab("Map");
                              setRecapEditMomentId(null);
                              setSideSheetActiveTab("main");
                            }
                          },
                        }}
                      />
                    ) : recapRateMomentId ? (
                      <RecapRateMomentScreen
                        embedded
                        momentId={recapRateMomentId}
                        navigation={{
                          goBack: () => setRecapRateMomentId(null),
                          replace: (screenName, params) => {
                            if (screenName === "CheckIn") {
                              setRecapRateMomentId(null);
                              setRecapEditMomentId(params?.momentId ?? null);
                            }
                          },
                          navigate: (screenName, params) => {
                            if (screenName === "CheckIn") {
                              setRecapRateMomentId(null);
                              setRecapEditMomentId(params?.momentId ?? null);
                            }
                          },
                        }}
                      />
                    ) : (
                      <RecapMomentScreen
                        embedded
                        onEmbeddedBack={handleDesktopRecapListBack}
                        navigation={{
                          goBack: handleDesktopRecapListBack,
                        }}
                        onNavigateToRateMoment={(momentId) => setRecapRateMomentId(momentId)}
                      />
                    )}
                  </View>
                ) : (
                <ScrollView style={styles.listScroll}>
                  {sideSheetActiveTab === "main" && (
                    searchQuery.trim() ? (
                      <View style={{ flex: 1, paddingTop: 0 }}>
                        <GooglePlacesSearchResults
                          results={combinedSearchResults}
                          isLoading={isGooglePlacesSearching}
                          error={googlePlacesError}
                          hasSearched={Boolean(searchQuery.trim())}
                          onPlaceSelect={handleGooglePlaceSelect}
                          onSaveToDisgo={handleSaveGooglePlace}
                          onAddRestaurant={() => {
                            handleClearSearch();
                            setShowAddRestaurantModal(true);
                          }}
                          currentLocation={currentLocation}
                          showDistance={true}
                          maxResults={20}
                          savedPlaces={savedGooglePlaces}
                          friendsAverageRatings={friendsAverageRatings}
                          userCheckinHistory={userCheckinHistory}
                          getFriendsRatingBadge={getFriendsRatingBadge}
                        />
                      </View>
                    ) : restaurantsResult?.isLoading ? (
                      <View style={styles.loadingContainer}>
                        <ActivityIndicator size="large" color="#6B4C3B" />
                        <Text style={styles.loadingText}>Loading restaurants...</Text>
                      </View>
                    ) : filteredRestaurants.length === 0 ? (
                      <View style={styles.emptyContainer}>
                        <Text style={styles.emptyText}>No restaurants found</Text>
                      </View>
                    ) : (
                      filteredRestaurants.map((restaurant) => {
                        if (!restaurant) return null;
                        return (
                          <RestaurantItem
                            key={restaurant._id}
                            item={restaurant}
                            onPress={handleRestaurantPress}
                            currentLocation={currentLocation}
                            mapRef={mapRef}
                            friendsAverageRatings={friendsAverageRatings}
                            userCheckinHistory={userCheckinHistory}
                            dailySpecials={dailySpecials}
                            hasGiftablePassesForRestaurant={hasGiftablePassesForRestaurant}
                            getFriendsRatingBadge={getFriendsRatingBadge}
                            formatDistance={formatDistanceDisplay}
                          />
                        );
                      })
                    )
                  )}

                  {sideSheetActiveTab === "passes" && <>{renderDisgoPassesExploreList()}</>}

                  {isSpeedRailzMapPromoUiEnabled && sideSheetActiveTab === "speedrailz" && (
                    <View style={{ flex: 1 }}>
                      <View style={{ alignItems: "center", paddingHorizontal: 12, paddingTop: 12, paddingBottom: 8 }}>
                        <Text
                          style={{
                            fontSize: 22,
                            fontFamily: "AbhayaLibre_700Bold, serif",
                            fontWeight: "700",
                            color: "#000",
                            textAlign: "center",
                          }}
                        >
                          {SPEEDRAILZ_SHEET_TITLE_LINE1}
                        </Text>
                        <Text
                          style={{
                            fontSize: 18,
                            fontFamily: "AbhayaLibre_700Bold, serif",
                            fontWeight: "700",
                            color: "#000",
                            textAlign: "center",
                            marginTop: 4,
                          }}
                        >
                          {SPEEDRAILZ_SHEET_TITLE_LINE2}
                        </Text>
                      </View>
                      {/* Tabs */}
                      <View style={styles.speedRailzTabs}>
                        <View style={[styles.speedRailzTab, styles.speedRailzTabActive]}>
                          <Text
                            style={[styles.speedRailzTabText, styles.speedRailzTabTextActive]}
                          >
                            Participating Bars
                          </Text>
                        </View>
                      </View>

                      {/* Tab Content */}
                      <ScrollView style={styles.speedRailzContent}>
                        {speedRailzRestaurants.length === 0 ? (
                            <View style={styles.emptyContainer}>
                              <Text style={styles.emptyText}>
                                No SpeedRailz restaurants available at this time.
                              </Text>
                            </View>
                          ) : (
                            speedRailzRestaurants.map((restaurant) => {
                              const restaurantWithDistance = currentLocation
                                ? {
                                  ...restaurant,
                                  distance: getDistance(
                                    currentLocation.latitude,
                                    currentLocation.longitude,
                                    restaurant.latitude,
                                    restaurant.longitude
                                  ),
                                }
                                : restaurant;

                              return (
                                <RestaurantItem
                                  key={restaurant._id}
                                  item={restaurantWithDistance}
                                  onPress={(r) => {
                                    setSideSheetActiveTab("main");
                                    handleRestaurantPress(r);
                                  }}
                                  currentLocation={currentLocation}
                                  mapRef={mapRef}
                                  friendsAverageRatings={friendsAverageRatings}
                                  userCheckinHistory={userCheckinHistory}
                                  dailySpecials={dailySpecials}
                                  hasGiftablePassesForRestaurant={hasGiftablePassesForRestaurant}
                                  getFriendsRatingBadge={getFriendsRatingBadge}
                                  formatDistance={formatDistanceDisplay}
                                  isSpeedRailzContext={true}
                                />
                              );
                            })
                          )
                        }
                      </ScrollView>
                    </View>
                  )}

                  {isHeavenHillsMapPromoUiEnabled && sideSheetActiveTab === "heavenhills" && (
                    <View style={{ flex: 1 }}>
                      <PalomaPaloozaSheetHeader
                        titleFontSize={22}
                        subtitleFontSize={18}
                        style={{ paddingTop: 8, paddingBottom: 4 }}
                      />
                      <View style={styles.speedRailzTabs}>
                        <View style={[styles.speedRailzTab, styles.palomaPaloozaTabActive]}>
                          <Text
                            style={[styles.speedRailzTabText, styles.palomaPaloozaTabTextActive]}
                          >
                            Participating restaurants
                          </Text>
                        </View>
                      </View>

                      <ScrollView style={styles.speedRailzContent}>
                        {heavenHillsRestaurants.length === 0 ? (
                            <View style={styles.emptyContainer}>
                              <Text style={styles.emptyText}>
                                No Paloma Palooza restaurants available at this time.
                              </Text>
                            </View>
                          ) : (
                            heavenHillsRestaurants.map((restaurant) => {
                              const restaurantWithDistance = currentLocation
                                ? {
                                  ...restaurant,
                                  distance: getDistance(
                                    currentLocation.latitude,
                                    currentLocation.longitude,
                                    restaurant.latitude,
                                    restaurant.longitude
                                  ),
                                }
                                : restaurant;

                              return (
                                <RestaurantItem
                                  key={restaurant._id}
                                  item={restaurantWithDistance}
                                  onPress={(r) => {
                                    setSideSheetActiveTab("main");
                                    handleRestaurantPress(r);
                                  }}
                                  currentLocation={currentLocation}
                                  mapRef={mapRef}
                                  friendsAverageRatings={friendsAverageRatings}
                                  userCheckinHistory={userCheckinHistory}
                                  dailySpecials={dailySpecials}
                                  hasGiftablePassesForRestaurant={hasGiftablePassesForRestaurant}
                                  getFriendsRatingBadge={getFriendsRatingBadge}
                                  formatDistance={formatDistanceDisplay}
                                  isHeavenHillsContext={true}
                                />
                              );
                            })
                          )
                        }
                      </ScrollView>
                    </View>
                  )}

                  {sideSheetActiveTab === "restaurant" && selectedRestaurant && (
                    <View style={{ flex: 1 }}>
                      <ScrollView
                        style={styles.listScroll}
                        contentContainerStyle={styles.bottomSheetScrollContent}
                        showsVerticalScrollIndicator={true}
                      >
                        <BottomSheetContent
                          selectedRestaurant={selectedRestaurant}
                          navigation={navigation}
                          handleDirections={handleDirections}
                          handleCenterMap={() => handleCenterMapOnRestaurant(selectedRestaurant)}
                          doesUserHaveMembership={doesUserHaveMembership}
                          getUserMembership={getUserMembership}
                          getCurrentMembershipTier={getCurrentMembershipTier}
                          getNextMembershipTier={getNextMembershipTier}
                          handleCheckIn={handleCheckIn}
                          handleClaimMembership={handleClaimMembership}
                          handleGiftPerk={handleGiftPerk}
                          handleBenefitPress={handleBenefitPress}
                          handleMissingPerk={handleMissingPerk}
                          userDoc={userDoc}
                          onUsePass={handleUsePass}
                          onCloseUsePassModal={handleCloseUsePassModal}
                          onClose={() => {
                            setSelectedRestaurant(null);
                            setSideSheetActiveTab(
                              previousTab && previousTab !== "restaurant" ? previousTab : "main"
                            );
                          }}
                          userCheckinHistory={userCheckinHistory}
                          authenticated={authenticated}
                          login={login}
                          hideCloseButton={true}
                        />
                      </ScrollView>
                    </View>
                  )}
                </ScrollView>
                )}
                </>
                )}
              </View>
            )}
          </View>
        </View>
      ) : (
        /* Mobile: Full-screen Map */
        <View style={styles.mapContainer}>
          <MapComponent
            restaurants={filteredRestaurants || []}
            selectedRestaurant={selectedRestaurant}
            onMarkerPress={handleMarkerPress}
            mapRef={mapRef}
            initialRegion={initialRegion}
            currentLocation={currentLocation}
            onRegionChange={handleMapRegionChange}
            userCheckinHistory={userCheckinHistory || []}
          />
        </View>
      )}

      {/* Mobile: Top UI Overlay */}
      {isMobile && (
        <View style={styles.topOverlay}>
          {/* Top Row: Filter | centered promo chips | Search + location */}
          <View style={[styles.topRow, { alignItems: "flex-start" }]}>
            <Pressable
              onPress={() => {
                handleClearSearch();
                setShowFilterSheet(true);
              }}
              style={styles.searchIconButton}
            >
              <Ionicons name="filter" size={20} color="#666" />
            </Pressable>

            {/* Center promo chips between filter and search (Paloma / SpeedRailz) */}
            <View style={[styles.topRowCenter, { minWidth: 0 }]}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                bounces={false}
                style={{ maxHeight: 44, alignSelf: "stretch", width: "100%" }}
                contentContainerStyle={{
                  flexGrow: 1,
                  minHeight: 40,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: 4,
                  gap: 6,
                }}
              >
                <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                  {isSpeedRailzMapPromoUiEnabled ? (
                    <Pressable
                      onPress={() => setShowSpeedRailzSheet(true)}
                      onPressIn={() => setIsSpeedRailzPressed(true)}
                      onPressOut={() => setIsSpeedRailzPressed(false)}
                      style={[
                        styles.speedRailzButton,
                        isSpeedRailzPressed && styles.speedRailzButtonPressed,
                      ]}
                    >
                      <Image
                        source={require("../assets/sr-blk-logo-1.png")}
                        style={styles.speedRailzLogo}
                        resizeMode="contain"
                      />
                      <Text style={styles.speedRailzButtonText}>Speedrailz</Text>
                    </Pressable>
                  ) : null}
                  {isHeavenHillsMapPromoUiEnabled ? (
                    <MapHeavenHillsButton onPress={() => setShowHeavenHillsSheet(true)} />
                  ) : null}
                </View>
              </ScrollView>
            </View>

            {/* Right: Search, then my location (aligned under search) */}
            <View style={{ alignItems: "flex-end" }}>
              <Pressable
                onPress={handleOpenSearch}
                style={styles.searchIconButton}
              >
                <Ionicons name="search" size={20} color="#666" />
              </Pressable>
              <Pressable
                onPress={handleCenterMap}
                style={[styles.searchIconButton, { marginTop: 8, position: "relative" }]}
              >
                <Ionicons
                  name={currentLocation ? "locate" : "locate-outline"}
                  size={20}
                  color={currentLocation ? "#4e2916" : "#666"}
                />
                {currentLocation ? (
                  <View style={styles.locationIndicatorSmall} />
                ) : null}
              </Pressable>
            </View>
          </View>

          {/* Web: House Pick inline results omitted — native-only (see floating House Pick → download app). */}
        </View>
      )}


      {/* Recap + House Pick — above bottom sheet (mobile); desktop uses sidebar */}
      {!selectedRestaurant && isMobile && (
        <View
          style={[
            styles.housePickButtonWrap,
            showDisgoPasses && styles.housePickButtonWrapFullWidth,
            {
              bottom: showRestaurantList || showPassesMobileSheet ? `${Math.max(listSheetHeight, 50)}%` : 90,
            },
          ]}
        >
          <View
            style={[
              styles.housePickAndRecapRow,
              showDisgoPasses && styles.housePickAndRecapRowThreeUp,
            ]}
          >
            {showDisgoPasses ? (
              <>
                <View style={styles.housePickRowSide}>
                  <RecapButton
                    pendingCount={Array.isArray(recapMoments) ? recapMoments.length : 0}
                    onPress={handleRecapPress}
                  />
                </View>
                <HousePickButton
                  onPress={() => setShowDownloadAppModal(true)}
                  containerStyle={styles.housePickRowCenter}
                />
                <View style={[styles.housePickRowSide, styles.housePickRowSideEnd]}>
                  <PassesExplorePill
                    isActive={showPassesMobileSheet}
                    availableTodayCount={passesAvailableNowCount}
                    onPress={() =>
                      handleDisgoPassesButtonPress({
                        isEnabled: showDisgoPasses,
                        onOpenPasses: () => {
                          if (showPassesMobileSheet) {
                            setShowPassesMobileSheet(false);
                          } else {
                            setListSheetHeight(50);
                            setIsListSheetExpanded(false);
                            setShowPassesMobileSheet(true);
                          }
                        },
                      })
                    }
                  />
                </View>
              </>
            ) : (
              <>
                <RecapButton
                  pendingCount={Array.isArray(recapMoments) ? recapMoments.length : 0}
                  onPress={handleRecapPress}
                />
                <HousePickButton onPress={() => setShowDownloadAppModal(true)} />
              </>
            )}
          </View>
        </View>
      )}

      {/* Loading Overlay when restaurant is clicked */}
      {isLoadingRestaurant && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 50,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <View
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: 16,
              padding: 24,
              alignItems: 'center',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 3.84,
              elevation: 5,
              minWidth: 120,
              minHeight: 120,
              justifyContent: 'center',
            }}
          >
            <ActivityIndicator size="large" color="#6B4C3B" />
            <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#6B4C3B', marginTop: 16 }}>
              Loading...
            </Text>
          </View>
        </View>
      )}

      {/* Filter Bottom Sheet */}
      {showFilterSheet && (
        <View style={[styles.bottomSheet, { height: "85%", zIndex: 30 }]}>
          <View style={styles.listHeader}>
            <View style={styles.listHeaderTop}>
              <View style={styles.listHeaderTopLeft} />
              <Text style={styles.listHeaderTitle}>Filters</Text>
              <TouchableOpacity
                onPress={() => setShowFilterSheet(false)}
                style={styles.listHeaderTopRight}
              >
                <Ionicons name="close" size={24} color="#6B4C3B" />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.mobileFilterSheetBody}>
            <FilterBottomSheetContent
              hideTitle
              showOnlySpeedRailzOnMap={showOnlySpeedRailzOnMap}
              setShowOnlySpeedRailzOnMap={setShowOnlySpeedRailzOnMap}
              showOnlyHeavenHillsOnMap={showOnlyHeavenHillsOnMap}
              setShowOnlyHeavenHillsOnMap={setShowOnlyHeavenHillsOnMap}
              showDisgoRestaurants={showDisgoRestaurants}
              setShowDisgoRestaurants={setShowDisgoRestaurants}
              showNonDisgoRestaurants={showNonDisgoRestaurants}
              setShowNonDisgoRestaurants={setShowNonDisgoRestaurants}
              showFriendsRatedRestaurants={showFriendsRatedRestaurants}
              setShowFriendsRatedRestaurants={setShowFriendsRatedRestaurants}
              showMyCheckedInRestaurants={showMyCheckedInRestaurants}
              setShowMyCheckedInRestaurants={setShowMyCheckedInRestaurants}
            />
          </View>
        </View>
      )}

      {/* "Ready to discover your next bite?" Button - Bottom Left (rendered after BottomSheet to appear behind it) */}
      {(!filteredRestaurants || filteredRestaurants.length === 0) &&
        !restaurantsResult?.isLoading &&
        !searchQuery.trim() && (
          <View style={{
            position: 'absolute',
            bottom: showRestaurantList || showPassesMobileSheet ? `${Math.max(listSheetHeight, 50)}%` : 90, // Position follows the bottom sheet's height dynamically, or above nav bar when closed
            left: 16,
            right: 80, // Leave space for location button on the right (16px padding + ~60px button + 16px padding + margin)
            zIndex: -1, // Render behind the bottom sheet using negative z-index
            paddingVertical: 4,
          }}>
            <TouchableOpacity
              onPress={handleOpenSearch}
              activeOpacity={0.8}
              style={{
                backgroundColor: 'rgba(107, 76, 59, 0.95)',
                borderRadius: 12,
                padding: 16,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25,
                shadowRadius: 3.84,
                elevation: 5,
                width: '100%',
              }}
            >
              <View style={{ width: '100%' }}>
                <Text style={{ color: '#FFFFFF', fontSize: 14 }}>
                  🍽️ Tap here to search for restaurants, cuisine, or specials nearby!
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        )}

      {/* Add Restaurant Modal */}
      {showAddRestaurantModal && (
        <AddRestaurantModal
          visible={showAddRestaurantModal}
          onClose={() => setShowAddRestaurantModal(false)}
          onSave={async (restaurantData) => {
            try {
              await addRestaurant(restaurantData);
              setShowAddRestaurantModal(false);
              Alert.alert("Success", "Restaurant added successfully!");
            } catch (error) {
              console.error("Error adding restaurant:", error);
              Alert.alert("Error", "Failed to add restaurant. Please try again.");
            }
          }}
        />
      )}

      {isMobile && (
        <BeRealCheckIn
          visible={showBeRealCheckIn}
          restaurant={restaurantForBeRealCheckIn}
          userPrivyDid={authData?.privyDid}
          userId={userDoc?._id}
          checkInLocation={beRealCheckInLocationSnapshot ?? undefined}
          desktopWebOptimized={false}
          onComplete={() => {
            setShowBeRealCheckIn(false);
            setRestaurantForBeRealCheckIn(null);
            setBeRealCheckInLocationSnapshot(null);
            setRestaurantForRecap(null);
            setShowDiscoModal(true);
          }}
          onCancel={() => {
            setShowBeRealCheckIn(false);
            setRestaurantForBeRealCheckIn(null);
            setBeRealCheckInLocationSnapshot(null);
            setRestaurantForRecap(null);
          }}
        />
      )}

      {/* Disco Success Modal */}
      <DiscoSuccessModal
        visible={showDiscoModal}
        onComplete={() => {
          setShowDiscoModal(false);
        }}
        duration={2000}
      />

      <GiftPassWorkflow
        visible={isGiftPassWorkflowVisible}
        onClose={handleCloseGiftPassWorkflow}
        pass={selectedPassForGift}
        selectedRestaurant={selectedRestaurant}
        restaurantName={selectedRestaurantForGift?.name}
      />
      <PassUseModal
        isOpen={showUsePassModal}
        onClose={handleCloseUsePassModal}
        pass={selectedPass}
        selectedRestaurant={selectedRestaurant}
        getUserMembership={getUserMembership}
        userDoc={userDoc}
      />

      {/* Too Far Modal */}
      <Modal
        visible={showTooFarModal}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowTooFarModal(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowTooFarModal(false)}
        >
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Oops, Too Far 📍</Text>
              <TouchableOpacity
                onPress={() => setShowTooFarModal(false)}
                style={styles.modalCloseButton}
              >
                <Ionicons name="close" size={28} color="#6B4C3B" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalBody}>
              <Text style={styles.modalText}>
                You need to be closer to {tooFarRestaurant?.name || 'the restaurant'} to check in.
              </Text>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Already Checked In Modal */}
      <Modal
        visible={showAlreadyCheckedInModal}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowAlreadyCheckedInModal(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowAlreadyCheckedInModal(false)}
        >
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Already Checked In ⏰</Text>
              <TouchableOpacity
                onPress={() => setShowAlreadyCheckedInModal(false)}
                style={styles.modalCloseButton}
              >
                <Ionicons name="close" size={28} color="#6B4C3B" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalBody}>
              <Text style={styles.modalText}>
                You've already checked in to {alreadyCheckedInRestaurant?.name || 'this restaurant'} today.
              </Text>
              <Text style={styles.modalSubtext}>
                Come back tomorrow for more points!
              </Text>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Mobile: Bottom Sheet for Restaurant List */}
      {isMobile && showRestaurantList && (
        <View style={[styles.bottomSheet, { height: `${listSheetHeight}%` }]}>

          {/* Header with Tabs */}
          <View style={styles.listHeader}>
            {/* Header Top Bar - Close button and Expand/Collapse button */}
            <View style={styles.listHeaderTop}>
              <View style={styles.listHeaderTopLeft} />
              <TouchableOpacity
                onPress={() => {
                  if (listSheetHeight >= 85) {
                    setListSheetHeight(50);
                    setIsListSheetExpanded(false);
                  } else {
                    setListSheetHeight(92);
                    setIsListSheetExpanded(true);
                  }
                }}
                style={styles.listExpandButton}
              >
                <Ionicons
                  name={listSheetHeight >= 85 ? "chevron-down" : "chevron-up"}
                  size={28}
                  color="#6B4C3B"
                />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  handleClearSearch();
                  setShowRestaurantList(false);
                }}
                style={styles.listCloseButton}
              >
                <Ionicons name="close" size={28} color="#6B4C3B" />
              </TouchableOpacity>
            </View>

            <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
              <View style={styles.searchBar}>
                <Ionicons name="search" size={20} color="#666" />
                <TextInput
                  ref={searchInputRef}
                  style={styles.searchInput}
                  placeholder="Search restaurants or cuisine..."
                  placeholderTextColor="#666"
                  value={searchQuery}
                  onChangeText={handleSearchQueryChange}
                />
                {searchQuery.length > 0 && (
                  <TouchableOpacity
                    onPress={handleClearSearch}
                    style={styles.clearButton}
                  >
                    <Ionicons name="close-circle" size={20} color="#666" />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {!searchQuery.trim() && (
              <>
                {/* Friend Rating Filter — above day tabs */}
                <View style={styles.filterContainer}>
                  {friendRatingFilterOptions.map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      onPress={() => setFriendRatingFilter(option.value)}
                      style={[
                        styles.friendFilterButton,
                        friendRatingFilter === option.value && styles.friendFilterButtonActive,
                      ]}
                    >
                      <Text
                        style={[
                          styles.filterText,
                          friendRatingFilter === option.value && styles.filterTextActive,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <View style={styles.dayTabsContainer}>
                  {days.map((day) => (
                    <TouchableOpacity
                      key={day}
                      onPress={() => setSelectedDay(day)}
                      style={[styles.dayTab, selectedDay === day && styles.dayTabActive]}
                    >
                      <Text
                        style={[styles.dayTabText, selectedDay === day && styles.dayTabTextActive]}
                      >
                        {getDayAbbreviation(day)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}

            <View style={styles.mainListHeaderSpacer} />
          </View>

          {/* Restaurant List / Search Results — keep nearby list until a query is typed */}
          {searchQuery.trim() ? (
            <View style={{ flex: 1, paddingBottom: 16 }}>
              <GooglePlacesSearchResults
                results={combinedSearchResults}
                isLoading={isGooglePlacesSearching}
                error={googlePlacesError}
                hasSearched={Boolean(searchQuery.trim())}
                onPlaceSelect={handleGooglePlaceSelect}
                onSaveToDisgo={handleSaveGooglePlace}
                onAddRestaurant={() => {
                  handleClearSearch();
                  setShowAddRestaurantModal(true);
                }}
                currentLocation={currentLocation}
                showDistance={true}
                maxResults={20}
                savedPlaces={savedGooglePlaces}
                friendsAverageRatings={friendsAverageRatings}
                userCheckinHistory={userCheckinHistory}
                getFriendsRatingBadge={getFriendsRatingBadge}
              />
            </View>
          ) : (
            <ScrollView
              style={styles.listScroll}
              contentContainerStyle={styles.mobileListSheetScrollContent}
            >
              {restaurantsResult?.isLoading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#6B4C3B" />
                  <Text style={styles.loadingText}>Loading restaurants...</Text>
                </View>
              ) : filteredRestaurants.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>No restaurants found</Text>
                </View>
              ) : (
                filteredRestaurants.map((restaurant) => {
                  if (!restaurant) return null;
                  return (
                    <RestaurantItem
                      key={restaurant._id}
                      item={restaurant}
                      onPress={handleRestaurantPress}
                      currentLocation={currentLocation}
                      mapRef={mapRef}
                      friendsAverageRatings={friendsAverageRatings}
                      userCheckinHistory={userCheckinHistory}
                      dailySpecials={dailySpecials}
                      hasGiftablePassesForRestaurant={hasGiftablePassesForRestaurant}
                      getFriendsRatingBadge={getFriendsRatingBadge}
                      formatDistance={formatDistanceDisplay}
                    />
                  );
                })
              )}
            </ScrollView>
          )}
        </View>
      )}

      {isMobile && showDisgoPasses && showPassesMobileSheet && (
        <View style={[styles.bottomSheet, { height: `${listSheetHeight}%`, zIndex: 95 }]}>
          <View style={styles.listHeader}>
            <View style={styles.listHeaderTop}>
              <View style={styles.listHeaderTopLeft} />
              <TouchableOpacity
                onPress={() => {
                  if (listSheetHeight >= 85) {
                    setListSheetHeight(50);
                    setIsListSheetExpanded(false);
                  } else {
                    setListSheetHeight(92);
                    setIsListSheetExpanded(true);
                  }
                }}
                style={styles.listExpandButton}
              >
                <Ionicons
                  name={listSheetHeight >= 85 ? "chevron-down" : "chevron-up"}
                  size={28}
                  color="#6B4C3B"
                />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setShowPassesMobileSheet(false)}
                style={styles.listCloseButton}
              >
                <Ionicons name="close" size={28} color="#6B4C3B" />
              </TouchableOpacity>
            </View>
            <Text
              style={{
                textAlign: "center",
                fontSize: 18,
                fontFamily: fontFamily.abhayaBold,
                color: "#4e2916",
                paddingBottom: 4,
              }}
            >
              Disgo Passes
            </Text>
            <Text
              style={{
                paddingHorizontal: 16,
                paddingBottom: 8,
                fontSize: 13,
                fontFamily: fontFamily.abhaya,
                color: "#9C8A80",
                textAlign: "center",
              }}
            >
              Live passes you can use in the current time window.
            </Text>
            <View style={styles.mainListHeaderSpacer} />
          </View>
          <ScrollView
            style={styles.listScroll}
            contentContainerStyle={styles.mobileListSheetScrollContent}
          >
            {renderDisgoPassesExploreList()}
          </ScrollView>
        </View>
      )}

      {/* HousePickBottomSheet: not mounted on web MapScreen — House Pick is native-only; CTAs use showDownloadAppModal */}

      {/* SpeedRailz Bottom Sheet */}
      {isSpeedRailzMapPromoUiEnabled && showSpeedRailzSheet && (
        <View style={styles.speedRailzSheet}>
          <View style={styles.speedRailzSheetHeader}>
            <View style={{ flex: 1, alignItems: "center" }}>
              <Text style={styles.speedRailzSheetTitle}>{SPEEDRAILZ_SHEET_TITLE_LINE1}</Text>
              <Text style={[styles.speedRailzSheetTitle, { fontSize: 20, marginTop: 2 }]}>{SPEEDRAILZ_SHEET_TITLE_LINE2}</Text>
            </View>
            <TouchableOpacity
              onPress={() => setShowSpeedRailzSheet(false)}
              style={styles.closeButton}
            >
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          {/* Tabs */}
          <View style={styles.speedRailzTabs}>
            <View style={[styles.speedRailzTab, styles.speedRailzTabActive]}>
              <Text
                style={[styles.speedRailzTabText, styles.speedRailzTabTextActive]}
              >
                Participating Bars
              </Text>
            </View>
          </View>

          {/* Tab Content */}
          <ScrollView style={styles.speedRailzContent}>
            {speedRailzRestaurants.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>
                    No SpeedRailz restaurants available at this time.
                  </Text>
                </View>
              ) : (
                speedRailzRestaurants.map((restaurant) => {
                  const restaurantWithDistance = currentLocation
                    ? {
                      ...restaurant,
                      distance: getDistance(
                        currentLocation.latitude,
                        currentLocation.longitude,
                        restaurant.latitude,
                        restaurant.longitude
                      ),
                    }
                    : restaurant;

                  return (
                    <RestaurantItem
                      key={restaurant._id}
                      item={restaurantWithDistance}
                      onPress={(r) => {
                        setShowSpeedRailzSheet(false);
                        handleRestaurantPress(r);
                      }}
                      currentLocation={currentLocation}
                      mapRef={mapRef}
                      friendsAverageRatings={friendsAverageRatings}
                      userCheckinHistory={userCheckinHistory}
                      dailySpecials={dailySpecials}
                      hasGiftablePassesForRestaurant={hasGiftablePassesForRestaurant}
                      getFriendsRatingBadge={getFriendsRatingBadge}
                      formatDistance={formatDistanceDisplay}
                      isSpeedRailzContext={true}
                    />
                  );
                })
              )
            }
          </ScrollView>
        </View>
      )}

      {isHeavenHillsMapPromoUiEnabled && showHeavenHillsSheet && (
        <View style={styles.speedRailzSheet}>
          <View style={styles.speedRailzSheetHeader}>
            <View style={{ flex: 1, alignItems: "center" }}>
              <PalomaPaloozaSheetHeader titleFontSize={22} subtitleFontSize={18} />
            </View>
            <TouchableOpacity
              onPress={() => setShowHeavenHillsSheet(false)}
              style={styles.closeButton}
            >
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          <View style={styles.speedRailzTabs}>
            <View style={[styles.speedRailzTab, styles.palomaPaloozaTabActive]}>
              <Text
                style={[styles.speedRailzTabText, styles.palomaPaloozaTabTextActive]}
              >
                Participating restaurants
              </Text>
            </View>
          </View>

          <ScrollView style={styles.speedRailzContent}>
            {heavenHillsRestaurants.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>
                    No Paloma Palooza restaurants available at this time.
                  </Text>
                </View>
              ) : (
                heavenHillsRestaurants.map((restaurant) => {
                  const restaurantWithDistance = currentLocation
                    ? {
                      ...restaurant,
                      distance: getDistance(
                        currentLocation.latitude,
                        currentLocation.longitude,
                        restaurant.latitude,
                        restaurant.longitude
                      ),
                    }
                    : restaurant;

                  return (
                    <RestaurantItem
                      key={restaurant._id}
                      item={restaurantWithDistance}
                      onPress={(r) => {
                        setShowHeavenHillsSheet(false);
                        handleRestaurantPress(r);
                      }}
                      currentLocation={currentLocation}
                      mapRef={mapRef}
                      friendsAverageRatings={friendsAverageRatings}
                      userCheckinHistory={userCheckinHistory}
                      dailySpecials={dailySpecials}
                      hasGiftablePassesForRestaurant={hasGiftablePassesForRestaurant}
                      getFriendsRatingBadge={getFriendsRatingBadge}
                      formatDistance={formatDistanceDisplay}
                      isHeavenHillsContext={true}
                    />
                  );
                })
              )
            }
          </ScrollView>
        </View>
      )}

      {/* Selected Restaurant Bottom Sheet - Only show on mobile */}
      {isMobile && selectedRestaurant && (
        <View style={styles.bottomSheetContainer}>
          <View style={styles.bottomSheetContent}>
            <ScrollView
              style={styles.bottomSheetScrollView}
              contentContainerStyle={styles.bottomSheetScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <BottomSheetContent
                selectedRestaurant={selectedRestaurant}
                navigation={navigation}
                handleDirections={handleDirections}
                handleCenterMap={handleCenterMap}
                doesUserHaveMembership={doesUserHaveMembership}
                getUserMembership={getUserMembership}
                getCurrentMembershipTier={getCurrentMembershipTier}
                getNextMembershipTier={getNextMembershipTier}
                handleCheckIn={handleCheckIn}
                handleClaimMembership={handleClaimMembership}
                handleGiftPerk={handleGiftPerk}
                handleBenefitPress={handleBenefitPress}
                handleMissingPerk={handleMissingPerk}
                userDoc={userDoc}
                onUsePass={handleUsePass}
                onCloseUsePassModal={handleCloseUsePassModal}
                onClose={handleCloseBottomSheet}
                userCheckinHistory={userCheckinHistory}
                authenticated={authenticated}
                login={login}
              />
            </ScrollView>
          </View>
        </View>
      )}

      {/* Bottom Navigation Bar - Only show on mobile */}
      {isMobile && (
        <SafeAreaView edges={["bottom"]} style={styles.bottomNavSafeArea}>
        <View style={styles.bottomNavBar}>
          <TouchableOpacity
            style={styles.bottomNavButton}
            onPress={() => {
              setActiveTab("Map");
              setShowPassesMobileSheet(false);
              setShowRestaurantList(true);
            }}
          >
            <Ionicons
              name={activeTab === "Map" ? "map" : "map-outline"}
              size={24}
              color={activeTab === "Map" ? "#4e2916" : "#999"}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.bottomNavButton}
            onPress={() => {
              setShowDownloadAppModal(true);
            }}
          >
            <Ionicons
              name="people-outline"
              size={28}
              color="#999"
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.bottomNavButton}
            onPress={() => {
              setShowDownloadAppModal(true);
            }}
          >
            <Ionicons
              name="notifications-outline"
              size={24}
              color="#999"
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.bottomNavButton}
            onPress={() => {
              if (!authenticated) {
                setShowWelcomeModal(true);
              } else {
                setActiveTab("Profile");
                setShowProfileModal(true);
              }
            }}
          >
            <Ionicons
              name={activeTab === "Profile" ? "person" : "person-outline"}
              size={24}
              color={activeTab === "Profile" ? "#4e2916" : "#999"}
            />
          </TouchableOpacity>
        </View>
        </SafeAreaView>
      )}

      {/* Download App Modal */}
      <Modal
        visible={showDownloadAppModal && !showRecapModal}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowDownloadAppModal(false)}
      >
        <Pressable
          style={styles.downloadAppModalOverlay}
          onPress={() => setShowDownloadAppModal(false)}
        >
          <Pressable style={styles.downloadAppModalCard} onPress={(e) => e.stopPropagation()}>
            <View style={styles.downloadAppModalAccent} />
            <TouchableOpacity
              onPress={() => setShowDownloadAppModal(false)}
              style={styles.downloadAppModalClose}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Ionicons name="close" size={22} color="#4e2916" />
            </TouchableOpacity>

            <ScrollView
              style={styles.downloadAppModalScroll}
              contentContainerStyle={styles.downloadAppModalScrollContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.downloadAppModalHero}>
                <View style={styles.downloadAppHeroIcon}>
                  <Ionicons name="sparkles" size={34} color="#6B4C3B" />
                </View>
                <Text style={styles.downloadAppEyebrow}>Disgo mobile</Text>
                <Text style={styles.downloadAppHeadline}>Take Disgo with you</Text>
                <Text style={styles.downloadAppSubcopy}>
                  The web map is great for browsing. For check-ins, recaps, friends, saved lists, and more, use
                  the app — it&apos;s free.
                </Text>
              </View>

              <View style={styles.downloadAppFeaturesBlock}>
                {DOWNLOAD_APP_FEATURES.map((row) => (
                  <View key={row.text} style={styles.downloadAppFeatureRow}>
                    <Ionicons
                      name={row.icon}
                      size={20}
                      color="#6B4C3B"
                      style={styles.downloadAppFeatureIcon}
                    />
                    <Text style={styles.downloadAppFeatureText}>{row.text}</Text>
                  </View>
                ))}
              </View>

              <Text style={styles.downloadAppStoreLabel}>Get the app</Text>
              <View style={styles.downloadAppStoreRow}>
                <TouchableOpacity
                  onPress={() => Linking.openURL("https://apps.apple.com/us/app/disgoapp/id6480529083")}
                  style={styles.downloadAppStoreButton}
                  accessibilityRole="link"
                  accessibilityLabel="Download on the App Store"
                >
                  <Image
                    source={require("../assets/store designs/apple.webp")}
                    style={styles.downloadAppStoreImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() =>
                    Linking.openURL("https://play.google.com/store/apps/details?id=com.disgoapp.disgo")
                  }
                  style={styles.downloadAppStoreButton}
                  accessibilityRole="link"
                  accessibilityLabel="Get it on Google Play"
                >
                  <Image
                    source={require("../assets/store designs/android.webp")}
                    style={styles.downloadAppStoreImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                onPress={() => setShowDownloadAppModal(false)}
                style={styles.downloadAppDismiss}
                accessibilityRole="button"
                accessibilityLabel="Continue on web"
              >
                <Text style={styles.downloadAppDismissText}>Not now — continue on web</Text>
              </TouchableOpacity>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        visible={showRecapModal}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={() => {
          if (recapEditMomentId) {
            setRecapEditMomentId(null);
            return;
          }
          if (recapRateMomentId) {
            setRecapRateMomentId(null);
            return;
          }
          setShowRecapModal(false);
        }}
      >
        {recapEditMomentId ? (
          <CheckInScreen
            route={{
              key: "recap-web-checkin",
              params: {
                momentId: recapEditMomentId,
                editMoment: true,
                embeddedWebRecap: true,
                onRequireAppDownload: () => {
                  setShowDownloadAppModal(true);
                },
              },
            }}
            navigation={{
              goBack: () => setRecapEditMomentId(null),
              setParams: () => {},
              navigate: (screenName, params) => {
                if (screenName === "Tabs" && params?.screen === "Social") {
                  setActiveTab("Map");
                  setShowRecapModal(false);
                  setRecapEditMomentId(null);
                }
              },
            }}
          />
        ) : recapRateMomentId ? (
          <RecapRateMomentScreen
            momentId={recapRateMomentId}
            navigation={{
              goBack: () => setRecapRateMomentId(null),
              replace: (screenName, params) => {
                if (screenName === "CheckIn") {
                  setRecapRateMomentId(null);
                  setRecapEditMomentId(params?.momentId ?? null);
                }
              },
              navigate: (screenName, params) => {
                if (screenName === "CheckIn") {
                  setRecapRateMomentId(null);
                  setRecapEditMomentId(params?.momentId ?? null);
                }
              },
            }}
          />
        ) : (
          <RecapMomentScreen
            navigation={{
              goBack: () => {
                setRecapEditMomentId(null);
                setRecapRateMomentId(null);
                setShowRecapModal(false);
              },
            }}
            onNavigateToRateMoment={(momentId) => setRecapRateMomentId(momentId)}
          />
        )}
        {showDownloadAppModal ? (
          <Pressable
            style={styles.downloadAppModalOverlay}
            onPress={() => setShowDownloadAppModal(false)}
          >
            <Pressable style={styles.downloadAppModalCard} onPress={(e) => e.stopPropagation()}>
              <View style={styles.downloadAppModalAccent} />
              <TouchableOpacity
                onPress={() => setShowDownloadAppModal(false)}
                style={styles.downloadAppModalClose}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <Ionicons name="close" size={22} color="#4e2916" />
              </TouchableOpacity>
              <ScrollView
                style={styles.downloadAppModalScroll}
                contentContainerStyle={styles.downloadAppModalScrollContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                <View style={styles.downloadAppModalHero}>
                  <View style={styles.downloadAppHeroIcon}>
                    <Ionicons name="sparkles" size={34} color="#6B4C3B" />
                  </View>
                  <Text style={styles.downloadAppEyebrow}>Disgo mobile</Text>
                  <Text style={styles.downloadAppHeadline}>Take Disgo with you</Text>
                  <Text style={styles.downloadAppSubcopy}>
                    The web map is great for browsing. For check-ins, recaps, friends, and passes, use
                    the app — it&apos;s free.
                  </Text>
                </View>
                <View style={styles.downloadAppFeaturesBlock}>
                  {DOWNLOAD_APP_FEATURES.map((row) => (
                    <View key={row.text} style={styles.downloadAppFeatureRow}>
                      <Ionicons
                        name={row.icon}
                        size={20}
                        color="#6B4C3B"
                        style={styles.downloadAppFeatureIcon}
                      />
                      <Text style={styles.downloadAppFeatureText}>{row.text}</Text>
                    </View>
                  ))}
                </View>
                <Text style={styles.downloadAppStoreLabel}>Get the app</Text>
                <View style={styles.downloadAppStoreRow}>
                  <TouchableOpacity
                    onPress={() => Linking.openURL("https://apps.apple.com/us/app/disgoapp/id6480529083")}
                    style={styles.downloadAppStoreButton}
                    accessibilityRole="link"
                    accessibilityLabel="Download on the App Store"
                  >
                    <Image
                      source={require("../assets/store designs/apple.webp")}
                      style={styles.downloadAppStoreImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() =>
                      Linking.openURL("https://play.google.com/store/apps/details?id=com.disgoapp.disgo")
                    }
                    style={styles.downloadAppStoreButton}
                    accessibilityRole="link"
                    accessibilityLabel="Get it on Google Play"
                  >
                    <Image
                      source={require("../assets/store designs/android.webp")}
                      style={styles.downloadAppStoreImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <TouchableOpacity
                  onPress={() => setShowDownloadAppModal(false)}
                  style={styles.downloadAppDismiss}
                  accessibilityRole="button"
                  accessibilityLabel="Continue on web"
                >
                  <Text style={styles.downloadAppDismissText}>Not now — continue on web</Text>
                </TouchableOpacity>
              </ScrollView>
            </Pressable>
          </Pressable>
        ) : null}
      </Modal>
      <Modal
        visible={showProfileModal}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowProfileModal(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowProfileModal(false)}
        >
          <Pressable style={styles.profileModalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.profileModalHeader}>
              <Text style={styles.profileModalTitle}>Profile</Text>
              <View style={styles.profileModalHeaderButtons}>
                <TouchableOpacity
                  onPress={() => { setSettingsPanel(null); setAccountSubPanel(null); setShowSettingsModal(true); }}
                  style={styles.modalHeaderButton}
                >
                  <Ionicons name="settings" size={24} color="#6B4C3B" />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setShowProfileModal(false)}
                  style={styles.modalCloseButton}
                >
                  <Ionicons name="close" size={28} color="#6B4C3B" />
                </TouchableOpacity>
              </View>
            </View>

            <ScrollView style={styles.profileModalScrollView} showsVerticalScrollIndicator={false}>
              {/* Profile Card */}
              <View style={styles.profileCard}>
                {/* Profile Picture */}
                <View style={styles.profilePictureContainer}>
                  <View style={styles.profilePicture}>
                    {profilePictureUrl ? (
                      <Image
                        source={{ uri: profilePictureUrl }}
                        style={styles.profilePictureImage}
                      />
                    ) : (
                      <Ionicons name="person" size={36} color="black" />
                    )}
                  </View>
                </View>

                {/* Username */}
                <Text style={styles.profileUsername}>
                  {userDoc?.username ? "@" + userDoc.username : "No username set"}
                </Text>

                {/* User Statistics */}
                {userStats && (
                  <View style={styles.profileStats}>
                    <View style={styles.profileStatItem}>
                      <Text style={styles.profileStatNumber}>
                        {userStats.friendsCount || 0}
                      </Text>
                      <Text style={styles.profileStatLabel}>friends</Text>
                    </View>

                    <View style={styles.profileStatDivider} />

                    <View style={styles.profileStatItem}>
                      <Text style={styles.profileStatNumber}>
                        {userStats.visitedCount || 0}
                      </Text>
                      <Text style={styles.profileStatLabel}>restaurants</Text>
                    </View>

                    <View style={styles.profileStatDivider} />

                    <View style={styles.profileStatItem}>
                      <Text style={styles.profileStatNumber}>
                        {userStats.followersCount || 0}
                      </Text>
                      <Text style={styles.profileStatLabel}>followers</Text>
                    </View>
                  </View>
                )}

                {/* Points Display — temporarily hidden
                <View style={styles.profilePoints}>
                  <View style={styles.profilePointsIcon}>
                    <Ionicons name="star" size={12} color="white" />
                  </View>
                  <View>
                    <Text style={styles.profilePointsLabel}>total disgo points</Text>
                    <Text style={styles.profilePointsValue}>
                      {userDoc?.disgoBalance || 0} points
                    </Text>
                  </View>
                </View>
                */}
              </View>

              {/* Tabs */}
              <View style={styles.profileTabs}>
                <TouchableOpacity
                  onPress={() => setProfileActiveTab('moments')}
                  style={[styles.profileTab, profileActiveTab === 'moments' && styles.profileTabActive]}
                >
                  <Text style={[
                    styles.profileTabText,
                    profileActiveTab === 'moments' && styles.profileTabTextActive
                  ]}>
                    bites
                  </Text>
                  {profileActiveTab === 'moments' && (
                    <View style={styles.profileTabIndicator} />
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setProfileActiveTab('visited')}
                  style={[styles.profileTab, profileActiveTab === 'visited' && styles.profileTabActive]}
                >
                  <Text style={[
                    styles.profileTabText,
                    profileActiveTab === 'visited' && styles.profileTabTextActive
                  ]}>
                    visits
                  </Text>
                  {profileActiveTab === 'visited' && (
                    <View style={styles.profileTabIndicator} />
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setProfileActiveTab('map')}
                  style={[styles.profileTab, profileActiveTab === 'map' && styles.profileTabActive]}
                >
                  <Text style={[
                    styles.profileTabText,
                    profileActiveTab === 'map' && styles.profileTabTextActive
                  ]}>
                    map
                  </Text>
                  {profileActiveTab === 'map' && (
                    <View style={styles.profileTabIndicator} />
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setProfileActiveTab('lists')}
                  style={[styles.profileTab, profileActiveTab === 'lists' && styles.profileTabActive]}
                >
                  <Text style={[
                    styles.profileTabText,
                    profileActiveTab === 'lists' && styles.profileTabTextActive
                  ]}>
                    lists
                  </Text>
                  {profileActiveTab === 'lists' && (
                    <View style={styles.profileTabIndicator} />
                  )}
                </TouchableOpacity>
              </View>

              {/* Tab Content */}
              <View style={styles.profileTabContent}>
                {/* Download App Promotional Card */}
                <View style={styles.appPromoCard}>
                  <View style={styles.appPromoHeader}>
                    <View style={styles.appPromoIcon}>
                      <View style={styles.appIconInner}>
                        <Ionicons name="sparkles" size={22} color="#6B4C3B" />
                      </View>
                    </View>
                    <View style={styles.appPromoTitleContainer}>
                      <Text style={styles.appPromoTitle}>Get the app</Text>
                      <Text style={styles.appPromoSubtitle}>
                        Check-ins, recaps, friends, and saved lists work best on mobile.
                      </Text>
                    </View>
                  </View>
                  <View style={styles.appPromoFeatures}>
                    {DOWNLOAD_APP_FEATURES.map((row) => (
                      <View key={row.text} style={styles.appPromoFeature}>
                        <Ionicons name={row.icon} size={16} color="#6B4C3B" />
                        <Text style={styles.appPromoFeatureText}>{row.text}</Text>
                      </View>
                    ))}
                  </View>
                  <View style={styles.appPromoButtons}>
                    <TouchableOpacity
                      onPress={() => Linking.openURL('https://apps.apple.com/us/app/disgoapp/id6480529083')}
                      style={styles.appStoreButtonWrapper}
                    >
                      <Image
                        source={require('../assets/store designs/apple.webp')}
                        style={styles.appStoreButtonImage}
                        resizeMode="contain"
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=com.disgoapp.disgo')}
                      style={styles.googlePlayButtonWrapper}
                    >
                      <Image
                        source={require('../assets/store designs/android.webp')}
                        style={styles.googlePlayButtonImage}
                        resizeMode="contain"
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Settings Modal */}
      <Modal
        visible={showSettingsModal}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowSettingsModal(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowSettingsModal(false)}
        >
          <Pressable style={styles.settingsModalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.profileModalHeader}>
              {settingsPanel ? (
                <TouchableOpacity
                  onPress={() => {
                    if (settingsPanel === "account" && accountSubPanel) {
                      setAccountSubPanel(null);
                    } else {
                      setSettingsPanel(null);
                    }
                  }}
                  style={styles.settingsModalBackButton}
                >
                  <Ionicons name="chevron-back" size={28} color="#6B4C3B" />
                </TouchableOpacity>
              ) : null}
              <Text style={styles.profileModalTitle}>
                {settingsPanel === "account"
                  ? (accountSubPanel === "details" ? "Details" : accountSubPanel === "logout" ? "Log out" : accountSubPanel === "delete" ? "Delete your account" : "Account")
                  : settingsPanel === "notifications"
                    ? "Notifications"
                    : "Settings"}
              </Text>
              <TouchableOpacity
                onPress={() => setShowSettingsModal(false)}
                style={styles.modalCloseButton}
              >
                <Ionicons name="close" size={28} color="#6B4C3B" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.settingsModalScrollView} showsVerticalScrollIndicator={false}>
              {settingsPanel === null ? (
                /* Settings menu: links grid first, then Account & Notifications */
                <View style={styles.settingsMenuContainer}>
                  <View style={styles.settingsLinksGrid}>
                    <TouchableOpacity
                      onPress={() => Linking.openURL("mailto:info@disgoapp.io")}
                      style={[styles.settingsLinkCell, styles.settingsLinkCellTopLeft]}
                    >
                      <Ionicons name="mail" size={24} color="#6B4C3B" />
                      <Text style={styles.settingsLinkCellText}>Contact Us</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => Linking.openURL("https://www.disgoapp.io/faqs")}
                      style={[styles.settingsLinkCell, styles.settingsLinkCellTopRight]}
                    >
                      <Ionicons name="help-circle" size={24} color="#6B4C3B" />
                      <Text style={styles.settingsLinkCellText}>Our FAQ</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => Linking.openURL("http://disgoapp.io/terms-of-service")}
                      style={[styles.settingsLinkCell, styles.settingsLinkCellBottomLeft]}
                    >
                      <Ionicons name="newspaper" size={24} color="#6B4C3B" />
                      <Text style={styles.settingsLinkCellText}>Terms of Service</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => Linking.openURL("http://disgoapp.io/privacy-policy")}
                      style={[styles.settingsLinkCell, styles.settingsLinkCellBottomRight]}
                    >
                      <Ionicons name="lock-closed" size={24} color="#6B4C3B" />
                      <Text style={styles.settingsLinkCellText}>Privacy Policy</Text>
                    </TouchableOpacity>
                  </View>

                  <TouchableOpacity
                    onPress={() => { setAccountSubPanel(null); setSettingsPanel("account"); }}
                    style={styles.settingsMenuItem}
                  >
                    <View style={styles.settingsMenuItemIcon}>
                      <Ionicons name="person" size={26} color="#6B4C3B" />
                    </View>
                    <Text style={styles.settingsMenuItemLabel}>Account</Text>
                    <Ionicons name="chevron-forward" size={22} color="#9ca3af" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setSettingsPanel("notifications")}
                    style={styles.settingsMenuItem}
                  >
                    <View style={styles.settingsMenuItemIcon}>
                      <Ionicons name="notifications" size={26} color="#6B4C3B" />
                    </View>
                    <Text style={styles.settingsMenuItemLabel}>Notifications</Text>
                    <Ionicons name="chevron-forward" size={22} color="#9ca3af" />
                  </TouchableOpacity>
                </View>
              ) : settingsPanel === "notifications" ? (
                /* Notifications panel - web has no push toggle; show message */
                <View style={styles.settingsSection}>
                  <Text style={styles.settingsNotificationMessage}>
                    Notification preferences are available in the Disgo mobile app.
                  </Text>
                </View>
              ) : (
                /* Account panel: sub-menu or Details / Delete your account */
                accountSubPanel === null ? (
                  <View style={styles.settingsMenuContainer}>
                    <TouchableOpacity
                      onPress={() => setAccountSubPanel("details")}
                      style={styles.settingsMenuItem}
                    >
                      <View style={styles.settingsMenuItemIcon}>
                        <Ionicons name="person-circle" size={26} color="#6B4C3B" />
                      </View>
                      <Text style={styles.settingsMenuItemLabel}>Details</Text>
                      <Ionicons name="chevron-forward" size={22} color="#9ca3af" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => setAccountSubPanel("logout")}
                      style={styles.settingsMenuItem}
                    >
                      <View style={styles.settingsMenuItemIcon}>
                        <Ionicons name="log-out" size={26} color="#6B4C3B" />
                      </View>
                      <Text style={styles.settingsMenuItemLabel}>Log out</Text>
                      <Ionicons name="chevron-forward" size={22} color="#9ca3af" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => setAccountSubPanel("delete")}
                      style={styles.settingsMenuItem}
                    >
                      <View style={styles.settingsMenuItemIcon}>
                        <Ionicons name="trash" size={26} color="#6B4C3B" />
                      </View>
                      <Text style={styles.settingsMenuItemLabel}>Delete your account</Text>
                      <Ionicons name="chevron-forward" size={22} color="#9ca3af" />
                    </TouchableOpacity>
                  </View>
                ) : accountSubPanel === "logout" ? (
                  <View style={styles.settingsSection}>
                    <Text style={styles.settingsNotificationMessage}>
                      Sign out of your account on this device.
                    </Text>
                    <TouchableOpacity
                      onPress={handleLogout}
                      style={[styles.settingsActionButton, { backgroundColor: "#fef2f2", borderWidth: 1, borderColor: "#fecaca" }]}
                    >
                      <Text style={[styles.settingsActionButtonText, { color: "#b91c1c" }]}>Log Out</Text>
                    </TouchableOpacity>
                  </View>
                ) : accountSubPanel === "delete" ? (
                  <View style={styles.settingsSection}>
                    <Text style={styles.settingsNotificationMessage}>
                      Permanently delete your account and all associated data. This cannot be undone.
                    </Text>
                    <TouchableOpacity
                      onPress={handleDeleteAccount}
                      style={[styles.settingsActionButton, { backgroundColor: "#fef2f2", borderWidth: 1, borderColor: "#fecaca" }]}
                    >
                      <Text style={[styles.settingsActionButtonText, { color: "#b91c1c" }]}>Delete Account</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  /* Details */
                  <>
                    <View style={styles.settingsSection}>
                      <TouchableOpacity
                        onPress={handleImagePicker}
                        style={styles.settingsProfilePictureContainer}
                      >
                        <View style={styles.settingsProfilePicture}>
                          {imagePreview ? (
                            <Image
                              source={{ uri: imagePreview }}
                              style={styles.settingsProfilePictureImage}
                            />
                          ) : profilePictureUrl ? (
                            <Image
                              source={{ uri: profilePictureUrl }}
                              style={styles.settingsProfilePictureImage}
                            />
                          ) : (
                            <Ionicons name="person" size={24} color="black" />
                          )}
                        </View>
                        <View style={styles.settingsProfilePictureCameraOverlay}>
                          <Ionicons name="camera" size={14} color="#FFFFFF" />
                        </View>
                      </TouchableOpacity>
                    </View>

                    <View style={styles.settingsField}>
                      <Text style={styles.settingsFieldLabel}>Name</Text>
                      <TextInput
                        placeholder="Name"
                        style={styles.settingsFieldInput}
                        value={userInputChanges.name}
                        onChangeText={(text) =>
                          setUserInputChanges({ ...userInputChanges, name: text })
                        }
                      />
                    </View>

                    <View style={styles.settingsField}>
                      <Text style={styles.settingsFieldLabel}>Username</Text>
                      <TextInput
                        placeholder="Username"
                        style={styles.settingsFieldInput}
                        value={userInputChanges.username?.toLowerCase()}
                        onChangeText={(text) => {
                          const sanitizedText = text.replace(/\s/g, '').toLowerCase();
                          setUserInputChanges({
                            ...userInputChanges,
                            username: sanitizedText,
                          });
                        }}
                        autoCapitalize="none"
                        maxLength={30}
                      />
                      {userInputChanges.username && userInputChanges.username.length >= 3 && (
                        <Text style={[
                          styles.usernameAvailability,
                          isUsernameAvailable === false && styles.usernameUnavailable
                        ]}>
                          {isUsernameAvailable === false ? "Username taken" : "Username available"}
                        </Text>
                      )}
                    </View>

                    <View style={styles.settingsField}>
                      <Text style={styles.settingsFieldLabel}>Email</Text>
                      <Text style={styles.settingsFieldValue}>
                        {userInputChanges.email || "Not linked"}
                      </Text>
                    </View>

                    <View style={styles.settingsField}>
                      <Text style={styles.settingsFieldLabel}>Phone Number</Text>
                      <Text style={styles.settingsFieldValue}>
                        {userInputChanges.phoneNumber || "Not linked"}
                      </Text>
                    </View>

                    <TouchableOpacity
                      onPress={handleSaveChanges}
                      style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <ActivityIndicator color="#FFFFFF" />
                      ) : (
                        <Text style={styles.saveButtonText}>Save Changes</Text>
                      )}
                    </TouchableOpacity>
                  </>
                )
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Welcome/Login Modal */}
      <Modal
        visible={showWelcomeModal && ready && !authenticated}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowWelcomeModal(false)}
      >
        <Pressable
          style={styles.welcomeModalOverlay}
          onPress={() => setShowWelcomeModal(false)}
        >
          <Pressable style={styles.welcomeModalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.welcomeModalHeader}>
              <Text style={styles.welcomeModalTitle}>Welcome to Disgo</Text>
              <TouchableOpacity
                onPress={() => setShowWelcomeModal(false)}
                style={styles.welcomeModalCloseButton}
              >
                <Ionicons name="close" size={28} color="#FFFFFF" />
              </TouchableOpacity>
            </View>

            <View style={styles.welcomeModalBody}>
              <Text style={styles.welcomeModalText}>
                To get started, please sign in to your account or download our mobile app for the best experience.
              </Text>

              <TouchableOpacity
                onPress={() => {
                  login();
                  setShowWelcomeModal(false);
                }}
                style={styles.welcomeLoginButton}
              >
                <Text style={styles.welcomeLoginButtonText}>Login / Sign Up</Text>
              </TouchableOpacity>

              <View style={styles.welcomeSeparator}>
                <View style={styles.welcomeSeparatorLine} />
                <Text style={styles.welcomeSeparatorText}>OR</Text>
                <View style={styles.welcomeSeparatorLine} />
              </View>

              <Text style={styles.welcomeDownloadText}>Download the App</Text>

              <View style={styles.welcomeAppStoreButtons}>
                <TouchableOpacity
                  onPress={() => Linking.openURL('https://apps.apple.com/us/app/disgoapp/id6480529083')}
                  style={styles.welcomeAppStoreButton}
                >
                  <Image
                    source={require('../assets/store designs/apple.webp')}
                    style={styles.welcomeAppStoreButtonImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=com.disgoapp.disgo')}
                  style={styles.welcomeAppStoreButton}
                >
                  <Image
                    source={require('../assets/store designs/android.webp')}
                    style={styles.welcomeAppStoreButtonImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9F5F3",
    width: "100%",
    height: "100vh",
  },
  containerDesktop: {
    backgroundColor: "#4e2916",
  },
  mapContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
    position: "relative",
  },
  mapWrapperDesktop: {
    position: "absolute",
    top: "5%",
    left: "2.5%",
    right: "2.5%",
    width: "95%",
    alignItems: "center",
  },
  mapAndListContainer: {
    flexDirection: "row",
    width: "100%",
    gap: 16,
    alignItems: "flex-start",
  },
  mapWrapperWithButtons: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  mapButtonsContainer: {
    width: "100%",
    marginBottom: 12,
    paddingHorizontal: 16,
  },
  mapContainerDesktop: {
    flex: 1,
    height: "80vh",
    borderRadius: 12,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    minWidth: 0,
  },
  rightSheetDesktop: {
    width: 400,
    height: "80vh",
    backgroundColor: "#F9F5F3",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    overflow: "hidden",
    flexDirection: "column",
  },
  rightSheetCheckInColumn: {
    flex: 1,
    minHeight: 0,
    flexDirection: "column",
  },
  rightSheetCheckInHeader: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  rightSheetCheckInTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 18,
    fontWeight: "700",
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_700Bold, serif",
    textAlign: "left",
  },
  rightSheetCheckInBody: {
    flex: 1,
    minHeight: 0,
  },
  mapTopButtonsContainer: {
    position: "absolute",
    top: 16,
    right: 16,
    flexDirection: "row",
    gap: 8,
    zIndex: 10,
  },
  reopenSideSheetButton: {
    backgroundColor: "#FFFFFF",
    borderRadius: 25,
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  topOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    padding: 16,
  },
  mapButtonsOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    padding: 16,
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  mapButtonsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  leftButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  housePickButtonWrap: {
    position: "absolute",
    left: 0,
    /* Sit above the map (default z=0) but behind all bottom sheets
       (restaurant list z=20, SpeedRailz/Paloma z=30, Passes mobile sheet z=95)
       so the Recap / House Pick / Passes pills don't overlap sheet content. */
    zIndex: 15,
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  housePickButtonWrapFullWidth: {
    right: 0,
  },
  housePickAndRecapRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 8,
    overflow: "visible",
  },
  housePickAndRecapRowThreeUp: {
    width: "100%",
    gap: 0,
  },
  housePickRowSide: {
    flex: 1,
    minWidth: 0,
    overflow: "visible",
    alignItems: "flex-start",
  },
  housePickRowSideEnd: {
    alignItems: "flex-end",
  },
  housePickRowCenter: {
    flexShrink: 0,
  },
  disgoPassesPillButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 999,
    paddingHorizontal: 12,
    height: 44,
    borderWidth: 2,
    borderColor: "#d3d3d3",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  disgoPassesPillButtonText: {
    marginLeft: 6,
    color: "#6B4C3B",
    fontSize: 15,
    lineHeight: 20,
    fontFamily: "AbhayaLibre_700Bold, serif",
    includeFontPadding: false,
  },
  disgoPassesPillButtonActive: {
    backgroundColor: "#6B4C3B",
    borderColor: "#6B4C3B",
  },
  disgoPassesPillButtonTextActive: {
    color: "#FFFFFF",
  },
  mainListHeaderSpacer: {
    marginBottom: 8,
  },
  topRowCenter: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  bottomRow: {
    flexDirection: "row",
  },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 25,
    paddingHorizontal: 16,
    paddingVertical: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
    marginBottom: 8,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 16,
    color: "#000000",
    fontFamily: "AbhayaLibre_400Regular, serif",
  },
  clearButton: {
    padding: 4,
  },
  searchIconButton: {
    backgroundColor: "#FFFFFF",
    borderRadius: 25,
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  speedRailzButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: 36,
    flexShrink: 0,
    backgroundColor: "#FEF3C7",
    borderWidth: 2,
    borderColor: "#FBBF24",
    borderRadius: 999,
    paddingHorizontal: 8,
  },
  speedRailzButtonPressed: {
    backgroundColor: "#FDE68A",
    borderColor: "#F59E0B",
  },
  speedRailzLogo: {
    width: 16,
    height: 16,
  },
  speedRailzButtonText: {
    marginLeft: 4,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "700",
    color: "#000000",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  locationButton: {
    position: 'relative',
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
    borderWidth: 2,
    borderColor: "#d3d3d3",
  },
  locationIndicator: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#4CAF50',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  locationIndicatorSmall: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4CAF50',
    borderWidth: 1.5,
    borderColor: '#FFFFFF',
  },
  speedRailzToggle: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
    borderWidth: 2,
    borderColor: "#d3d3d3",
  },
  speedRailzToggleActive: {
    backgroundColor: "#D4C4B8",
    borderColor: "#6B4C3B",
  },
  speedRailzTogglePressed: {
    backgroundColor: "#9B7F6B",
    borderColor: "#6B4C3B",
  },
  speedRailzToggleLogo: {
    width: 40,
    height: 40,
  },
  listHeaderTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    marginBottom: 8,
  },
  /** Desktop sidebar: single header row (close + chips wrap together) */
  desktopSideSheetHeaderTop: {
    alignItems: "stretch",
    justifyContent: "flex-start",
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 6,
    marginBottom: 4,
  },
  desktopSideSheetCloseRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
  },
  sideSheetNavBar: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    alignContent: "flex-start",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  sideSheetNavButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 0,
    borderRadius: 8,
    backgroundColor: "#f5f0ed",
  },
  sideSheetNavButtonFixed: {
    width: 109.33,
    height: 40,
    flexShrink: 0,
  },
  /** Slightly darker + border so close reads separately from tab chips */
  sideSheetCloseButtonDistinct: {
    backgroundColor: "#DDD4CB",
    borderWidth: 1,
    borderColor: "#C4B5A8",
  },
  sideSheetNavButtonActive: {
    backgroundColor: "#e8ddd4",
  },
  sideSheetNavButtonLabel: {
    fontSize: 12,
    fontFamily: "AbhayaLibre_600SemiBold, serif",
    color: "#666",
    flexShrink: 1,
    minWidth: 0,
    maxWidth: 66,
  },
  sideSheetNavButtonLabelActive: {
    color: "#4e2916",
  },
  listHeaderTopLeft: {
    flex: 1,
  },
  listHeaderTitle: {
    flex: 1,
    fontSize: 20,
    fontWeight: '700',
    color: '#6B4C3B',
    fontFamily: 'AbhayaLibre_700Bold, serif',
    textAlign: 'center',
  },
  listHeaderTopRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
  listExpandButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 2,
  },
  listCloseButton: {
    flex: 1,
    alignItems: "flex-end",
    justifyContent: "center",
    padding: 2,
  },
  bottomSheet: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#F9F5F3",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 20,
  },
  listHeader: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  tabContainer: {
    flexDirection: "row",
    backgroundColor: "#e5e5e5",
    borderRadius: 8,
    padding: 5,
    marginBottom: 8,
  },
  tabButton: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 10,
    borderRadius: 6,
  },
  tabButtonActive: {
    backgroundColor: "#6B4C3B",
  },
  tabText: {
    fontSize: 17,
    fontWeight: "700",
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  tabTextActive: {
    color: "#FFFFFF",
  },
  dayTabsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  dayTab: {
    flex: 1,
    alignItems: "center",
    paddingBottom: 8,
    borderBottomWidth: 0,
  },
  dayTabActive: {
    borderBottomWidth: 2,
    borderBottomColor: "#6B4C3B",
  },
  dayTabText: {
    fontSize: 17,
    fontWeight: "400",
    color: "#999",
    fontFamily: "AbhayaLibre_400Regular, serif",
  },
  dayTabTextActive: {
    fontWeight: "600",
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_600SemiBold, serif",
  },
  filterContainer: {
    flexDirection: "row",
    backgroundColor: "#e5e5e5",
    borderRadius: 8,
    padding: 5,
    marginBottom: 8,
  },
  friendFilterButton: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 9,
    borderRadius: 6,
  },
  friendFilterButtonActive: {
    backgroundColor: "#6B4C3B",
  },
  filterText: {
    fontSize: 16,
    fontWeight: "700",
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  filterTextActive: {
    color: "#FFFFFF",
  },
  desktopFilterSheetBody: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 0,
  },
  desktopRecapSheetBody: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  mobileFilterSheetBody: {
    flex: 1,
    minHeight: 0,
  },
  listScroll: {
    flex: 1,
  },
  /** Extra space so list/passes scroll above the floating bottom nav on mobile web */
  mobileListSheetScrollContent: {
    paddingBottom: 120,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: "#666",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  emptyText: {
    fontSize: 16,
    color: "#666",
  },
  restaurantItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#e0e0e0",
    backgroundColor: "transparent",
    overflow: "hidden",
  },
  restaurantItemSpeedRailz: {
    borderColor: "#FBBF24",
    backgroundColor: "#FEF3C7",
  },
  restaurantItemHeavenHills: {
    borderColor: PALOMA_ACCENT,
    backgroundColor: PALOMA_ROW_BG,
  },
  restaurantImageContainer: {
    position: "relative",
    marginRight: 16,
  },
  restaurantImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
  },
  restaurantImageStyle: {
    borderRadius: 8,
  },
  giftIconOverlay: {
    position: "absolute",
    bottom: 2,
    right: 2,
    backgroundColor: "#6B4C3B",
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "white",
  },
  partnerBadgeOverlay: {
    position: "absolute",
    top: -4,
    right: -4,
    backgroundColor: "#6B4C3B",
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  partnerBadgeText: {
    color: "#fff",
    fontSize: 12,
    fontFamily: "AbhayaLibre_700Bold, serif",
    fontWeight: "700",
  },
  restaurantInfo: {
    flex: 1,
    paddingRight: 8,
  },
  restaurantHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 4,
  },
  restaurantNameRow: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
    flex: 1,
  },
  restaurantName: {
    fontSize: 18,
    fontWeight: "700",
    color: "#000000",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  visitedPill: {
    backgroundColor: colors.brownMuted,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  visitedPillText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#FFFFFF",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  restaurantDistance: {
    fontSize: 12,
    color: "#666666",
    fontFamily: "AbhayaLibre_400Regular, serif",
    marginLeft: 8,
  },
  restaurantDistanceSpeedRailz: {
    color: "#000000",
  },
  restaurantDistanceHeavenHills: {
    color: PALOMA_NAVY,
  },
  restaurantMeta: {
    fontSize: 14,
    color: "#666666",
    fontFamily: "AbhayaLibre_400Regular, serif",
    marginBottom: 4,
  },
  happyHourContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
    flexWrap: "wrap",
    gap: 8,
  },
  happyHourBadge: {
    backgroundColor: "#6B4C3B",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  happyHourBadgeText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#FFFFFF",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  happyHourDescription: {
    backgroundColor: "#6B4C3B",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  happyHourDescriptionText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#FFFFFF",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  speedRailzSheet: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "85%",
    backgroundColor: "#F9F5F3",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 30,
  },
  closeButton: {
    padding: 4,
  },
  speedRailzSheetHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  speedRailzSheetTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#000000",
    fontFamily: "AbhayaLibre_700Bold, serif",
    textAlign: "center",
    flex: 1,
  },
  speedRailzTabs: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    marginBottom: 16,
  },
  speedRailzTab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: "center",
    position: "relative",
  },
  speedRailzTabActive: {
    borderBottomWidth: 2,
    borderBottomColor: "#92400E",
  },
  speedRailzTabText: {
    fontSize: 18,
    fontWeight: "700",
    color: "#000000",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  speedRailzTabTextActive: {
    color: "#92400E",
  },
  palomaPaloozaTabActive: {
    borderBottomWidth: 2,
    borderBottomColor: PALOMA_ACCENT,
  },
  palomaPaloozaTabTextActive: {
    color: PALOMA_NAVY,
  },
  speedRailzContent: {
    flex: 1,
    paddingBottom: 100,
  },
  bottomSheetContainer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "92%",
    maxHeight: "98%",
    backgroundColor: "#F9F5F3",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 20,
    overflow: "hidden",
  },
  bottomSheetContent: {
    flex: 1,
    width: "100%",
  },
  bottomSheetScrollView: {
    flex: 1,
  },
  bottomSheetScrollContent: {
    paddingBottom: 100,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  skeletonContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#e0e0e0",
    backgroundColor: "#F9F5F3",
  },
  skeletonImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    backgroundColor: "#e0e0e0",
    marginRight: 16,
  },
  skeletonContent: {
    flex: 1,
  },
  skeletonLine1: {
    width: "60%",
    height: 16,
    backgroundColor: "#e0e0e0",
    borderRadius: 4,
    marginBottom: 8,
  },
  skeletonLine2: {
    width: "80%",
    height: 12,
    backgroundColor: "#e0e0e0",
    borderRadius: 4,
    marginBottom: 4,
  },
  skeletonLine3: {
    width: "40%",
    height: 12,
    backgroundColor: "#e0e0e0",
    borderRadius: 4,
  },
  bottomNavSafeArea: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 100,
  },
  bottomNavBar: {
    marginBottom: 20,
    marginHorizontal: 20,
    backgroundColor: "#ffffff",
    borderRadius: 15,
    height: 60,
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 0 },
    paddingHorizontal: 15,
    elevation: 8,
  },
  bottomNavButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#F9F5F3",
    borderRadius: 20,
    width: "90%",
    maxWidth: 500,
    maxHeight: "80%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  modalTitle: {
    fontSize: 24,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#6B4C3B",
  },
  modalCloseButton: {
    padding: 4,
  },
  modalBody: {
    padding: 20,
  },
  modalText: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#4d2c1d",
    marginBottom: 12,
    textAlign: "center",
  },
  modalSubtext: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#6b7280",
    textAlign: "center",
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 20,
    paddingTop: 0,
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  modalButtonPrimary: {
    backgroundColor: "#6B4C3B",
  },
  modalButtonSecondary: {
    backgroundColor: "#e5e7eb",
  },
  modalButtonTextPrimary: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#ffffff",
  },
  modalButtonTextSecondary: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#4d2c1d",
  },
  modalScrollView: {
    maxHeight: "70vh",
  },
  downloadAppModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(22, 14, 10, 0.52)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  downloadAppModalCard: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#FFFCF9",
    borderRadius: 24,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(107, 76, 59, 0.16)",
    shadowColor: "#1a0f0a",
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.2,
    shadowRadius: 28,
    elevation: 16,
    position: "relative",
    transform: [{ translateY: -36 }],
  },
  downloadAppModalAccent: {
    height: 5,
    width: "100%",
    backgroundColor: "#6B4C3B",
  },
  downloadAppModalClose: {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(255, 252, 249, 0.96)",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(107, 76, 59, 0.12)",
  },
  downloadAppModalScroll: {
    maxHeight: "78vh",
  },
  downloadAppModalScrollContent: {
    paddingHorizontal: 24,
    paddingTop: 22,
    paddingBottom: 22,
  },
  downloadAppModalHero: {
    alignItems: "center",
    marginBottom: 4,
  },
  downloadAppHeroIcon: {
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: "rgba(107, 76, 59, 0.1)",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 14,
  },
  downloadAppEyebrow: {
    fontSize: 11,
    letterSpacing: 2.2,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#8B6F47",
    marginBottom: 6,
    textTransform: "uppercase",
  },
  downloadAppHeadline: {
    fontSize: 28,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#4e2916",
    textAlign: "center",
    marginBottom: 10,
    lineHeight: 30,
  },
  downloadAppSubcopy: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#6B5346",
    textAlign: "center",
    lineHeight: 22,
    maxWidth: 340,
  },
  downloadAppFeaturesBlock: {
    width: "100%",
    backgroundColor: "rgba(107, 76, 59, 0.06)",
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginTop: 22,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "rgba(107, 76, 59, 0.08)",
  },
  downloadAppFeatureRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 8,
  },
  downloadAppFeatureIcon: {
    marginTop: 1,
    marginRight: 12,
  },
  downloadAppFeatureText: {
    flex: 1,
    fontSize: 15,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#4d2c1d",
    lineHeight: 21,
  },
  downloadAppStoreLabel: {
    fontSize: 13,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#8B6F47",
    letterSpacing: 0.3,
    marginBottom: 10,
    textAlign: "center",
  },
  downloadAppStoreRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 6,
  },
  downloadAppStoreButton: {
    flex: 1,
    minWidth: 0,
  },
  downloadAppStoreImage: {
    width: "100%",
    height: 52,
  },
  downloadAppDismiss: {
    alignSelf: "center",
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginTop: 4,
  },
  downloadAppDismissText: {
    fontSize: 15,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#8B6F47",
    textDecorationLine: "underline",
    textDecorationColor: "rgba(139, 111, 71, 0.45)",
  },
  appPromoCardCompact: {
    backgroundColor: "#F9F5F3",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#6B4C3B",
    padding: 20,
    margin: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  appPromoHeaderCompact: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  appPromoIconCompact: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: "#6B4C3B",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 14,
  },
  appIconInnerCompact: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#8B6F47",
    justifyContent: "center",
    alignItems: "center",
  },
  appPromoTitleContainerCompact: {
    flex: 1,
  },
  appPromoSubtitleCompact: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#8B6F47",
  },
  appPromoFeaturesCompact: {
    marginBottom: 16,
  },
  appPromoFeatureCompact: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  appPromoFeatureTextCompact: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#6B4C3B",
    marginLeft: 8,
    flex: 1,
  },
  appPromoButtonsCompact: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 8,
  },
  appStoreButtonWrapperCompact: {
    flex: 1,
  },
  appStoreButtonImageCompact: {
    width: "100%",
    height: 48,
  },
  googlePlayButtonWrapperCompact: {
    flex: 1,
  },
  googlePlayButtonImageCompact: {
    width: "100%",
    height: 48,
  },
  appPromoCard: {
    backgroundColor: "#F9F5F3",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#6B4C3B",
    padding: 12,
    margin: 16,
  },
  appPromoHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  appPromoIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: "#6B4C3B",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  appIconInner: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: "#8B6F47",
    justifyContent: "center",
    alignItems: "center",
  },
  appPromoTitleContainer: {
    flex: 1,
  },
  appPromoTitle: {
    fontSize: 20,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#6B4C3B",
    marginBottom: 4,
  },
  appPromoSubtitle: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#8B6F47",
  },
  appPromoFeatures: {
    marginBottom: 12,
  },
  appPromoFeature: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  appPromoFeatureText: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#6B4C3B",
    marginLeft: 8,
  },
  appPromoButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
  },
  appStoreButtonWrapper: {
    flex: 1,
  },
  appStoreButtonImage: {
    width: "100%",
    height: 50,
  },
  googlePlayButtonWrapper: {
    flex: 1,
  },
  googlePlayButtonImage: {
    width: "100%",
    height: 50,
  },
  leaderboardContainer: {
    padding: 16,
  },
  top3Title: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
    textAlign: "center",
    marginBottom: 4,
  },
  lastUpdatedText: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
    textAlign: "center",
    marginBottom: 12,
  },
  top3Container: {
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    paddingTop: 20,
    marginBottom: 20,
  },
  top3Card: {
    width: 113,
    height: 126,
    marginBottom: 8,
    marginHorizontal: 4,
    position: "relative",
  },
  rankBadge: {
    position: "absolute",
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    top: -20,
    left: "50%",
    marginLeft: -20,
    zIndex: 10,
  },
  medalEmoji: {
    fontSize: 20,
  },
  top3ImageBackground: {
    width: "100%",
    height: "100%",
    borderRadius: 16,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: "#FBBF24",
    justifyContent: "flex-end",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  top3Overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
  },
  top3Info: {
    padding: 16,
    alignItems: "center",
  },
  top3Name: {
    fontSize: 20,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
    marginBottom: 4,
    textAlign: "center",
    lineHeight: 22,
  },
  top3Points: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#000000",
  },
  profileModalContent: {
    backgroundColor: "#F9F5F3",
    borderRadius: 20,
    width: "90%",
    maxWidth: 600,
    maxHeight: "85%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  profileModalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  profileModalTitle: {
    fontSize: 24,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#6B4C3B",
  },
  profileModalScrollView: {
    maxHeight: "75vh",
  },
  profileCard: {
    backgroundColor: "#F0EBE6",
    borderRadius: 16,
    padding: 16,
    paddingTop: 64,
    margin: 20,
    marginTop: 32,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  profilePictureContainer: {
    alignItems: "center",
    marginTop: -80,
    marginBottom: 12,
  },
  profilePicture: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: "#B8A79B",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 4,
    borderColor: "#F0EBE6",
  },
  profilePictureImage: {
    width: "100%",
    height: "100%",
    borderRadius: 48,
  },
  profileUsername: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
    textAlign: "center",
    marginBottom: 16,
  },
  profileStats: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 16,
  },
  profileStatItem: {
    alignItems: "center",
    flex: 1,
  },
  profileStatNumber: {
    fontSize: 20,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
  },
  profileStatLabel: {
    fontSize: 12,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#000000",
  },
  profileStatDivider: {
    width: 1,
    height: 24,
    backgroundColor: "#d1d5db",
    alignSelf: "center",
  },
  profilePoints: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  profilePointsIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#6B4C3B",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  profilePointsLabel: {
    fontSize: 12,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#000000",
  },
  profilePointsValue: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
  },
  profileTabs: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingHorizontal: 20,
  },
  profileTab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    position: "relative",
  },
  profileTabActive: {
    // Active state styling
  },
  profileTabText: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#6b7280",
    textAlign: "center",
  },
  profileTabTextActive: {
    color: "#78350f",
  },
  profileTabIndicator: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: "#78350f",
  },
  profileTabContent: {
    padding: 20,
    minHeight: 200,
  },
  profileTabPlaceholder: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 40,
  },
  profileTabPlaceholderText: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#6b7280",
    textAlign: "center",
  },
  profileModalHeaderButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  modalHeaderButton: {
    padding: 4,
  },
  settingsModalContent: {
    backgroundColor: "#F9F5F3",
    borderRadius: 20,
    width: "90%",
    maxWidth: 500,
    maxHeight: "80%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  settingsModalScrollView: {
    maxHeight: "70vh",
  },
  settingsModalBackButton: {
    padding: 4,
    marginRight: 8,
  },
  settingsMenuContainer: {
    padding: 20,
  },
  settingsMenuItem: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: "#F0EBE6",
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  settingsMenuItemIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#e5e7eb",
    justifyContent: "center",
    alignItems: "center",
  },
  settingsMenuItemLabel: {
    flex: 1,
    marginLeft: 16,
    fontSize: 20,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
  },
  settingsNotificationMessage: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#6b7280",
    textAlign: "center",
    paddingVertical: 24,
    paddingHorizontal: 20,
  },
  settingsSection: {
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  settingsProfilePictureContainer: {
    position: "relative",
    alignSelf: "center",
    marginBottom: 8,
  },
  settingsProfilePictureCameraOverlay: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#6B4C3B",
    borderWidth: 2,
    borderColor: "#F9F5F3",
    justifyContent: "center",
    alignItems: "center",
  },
  settingsProfilePicture: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "#B8A79B",
    justifyContent: "center",
    alignItems: "center",
  },
  settingsProfilePictureImage: {
    width: "100%",
    height: "100%",
    borderRadius: 32,
  },
  editPictureButton: {
    marginTop: 8,
  },
  editPictureButtonText: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#78350f",
    textDecorationLine: "underline",
  },
  settingsField: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
  },
  settingsFieldLabel: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#000000",
    width: "33%",
  },
  settingsFieldInput: {
    flex: 1,
    paddingLeft: 8,
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#000000",
  },
  settingsFieldValue: {
    flex: 1,
    paddingLeft: 8,
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#6b7280",
  },
  usernameAvailability: {
    fontSize: 12,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#10b981",
    marginTop: 4,
    marginLeft: "33%",
  },
  usernameUnavailable: {
    color: "#ef4444",
  },
  settingsLinksGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
  },
  settingsLinkCell: {
    width: "50%",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
    paddingHorizontal: 12,
  },
  settingsLinkCellTopLeft: {
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: "#e5e7eb",
  },
  settingsLinkCellTopRight: {
    borderBottomWidth: 1,
    borderColor: "#e5e7eb",
  },
  settingsLinkCellBottomLeft: {
    borderRightWidth: 1,
    borderColor: "#e5e7eb",
  },
  settingsLinkCellBottomRight: {},
  settingsLinkCellText: {
    marginTop: 8,
    fontSize: 16,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#1e40af",
    textDecorationLine: "underline",
    textAlign: "center",
  },
  settingsLink: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
  },
  settingsLinkText: {
    flex: 1,
    paddingLeft: 8,
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#1e40af",
    textDecorationLine: "underline",
  },
  saveButton: {
    backgroundColor: "#6B4C3B",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 32,
    margin: 16,
    marginTop: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#FFFFFF",
  },
  settingsActionButton: {
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    alignItems: "center",
  },
  settingsActionButtonText: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#dc2626",
    textDecorationLine: "underline",
  },
  welcomeModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
  welcomeModalContent: {
    backgroundColor: "#4e2916",
    borderRadius: 20,
    width: "90%",
    maxWidth: 500,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  welcomeModalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
  },
  welcomeModalTitle: {
    fontSize: 28,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#FFFFFF",
  },
  welcomeModalCloseButton: {
    padding: 4,
  },
  welcomeModalBody: {
    padding: 24,
    alignItems: "center",
  },
  welcomeModalText: {
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 24,
  },
  welcomeLoginButton: {
    backgroundColor: "#8B6F47",
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 32,
    width: "100%",
    alignItems: "center",
    marginBottom: 24,
  },
  welcomeLoginButtonText: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#FFFFFF",
  },
  welcomeSeparator: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    marginBottom: 24,
  },
  welcomeSeparatorLine: {
    flex: 1,
    height: 1,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
  },
  welcomeSeparatorText: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_400Regular",
    color: "#FFFFFF",
    paddingHorizontal: 16,
  },
  welcomeDownloadText: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold",
    color: "#FFFFFF",
    marginBottom: 16,
  },
  welcomeAppStoreButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    gap: 12,
  },
  welcomeAppStoreButton: {
    flex: 1,
    height: 50,
  },
  welcomeAppStoreButtonImage: {
    width: "100%",
    height: "100%",
  },
});

export default MapScreen;
